import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { API } from "../../../cache";
import { downloadAttachment } from "../../../shared/utils";
import { tasksApi } from "../api/tasksApi";
import AttachmentsModal from "../../../AttachmentsModal";
import EffortEstimatePanel from "../../../EffortEstimatePanel";
import { TimesheetEntryModal } from "../../../DailyTimesheetView";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { DrawerTabs, FilterTriggerButton, InlineCell } from "../../common/components";
import EntityDiscussionsTab from "../../common/components/EntityDiscussionsTab";
import EntityTagsField from "../../common/components/EntityTagsField";
import { createTagMapping, listTags } from "../../common/services/tagsApi";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";
import SavedFiltersPanel from "../../common/components/SavedFiltersPanel";
import DueDateChangeModal from "../../common/components/DueDateChangeModal";
import DueDateChangeBadge from "../../common/components/DueDateChangeBadge";
import MultiProjectTaskModal from "../components/MultiProjectTaskModal";
import DefectQuickViewDrawer from "../../defects/components/DefectQuickViewDrawer";
import {
  areAllBoardColsCollapsed,
  buildCollapsedBoardCols,
  buildFilteredTasks,
  buildFilterFields,
  buildSubFeatureOptions,
  buildVisibleFilterFields,
  countActiveFilters,
  getCollapseAllSet,
} from "../services/tasksViewDerivations";
import {
  BOARD_COLS,
  DEFAULT_ALL_TASKS_APPLIED_FILTERS,
  FILTER_FIELDS,
  IMPL_STATUS_OPTIONS,
  REPORTING_CHANNEL_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_DOT,
  STATUS_PILL_CLASS,
  TASK_VISIBILITY_OPTIONS,
  TYPE_OPTIONS,
} from "../constants/tasksViewV2Constants";
import "../../../MasterViewV2.css";
import "../../../MasterView.css";
import "../../../DailyTimesheetView.css";
import "../../../V2SideDrawer.css";

// ─── helpers ────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function avatarColor(name) {
  if (!name) return 1;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return (h % 5) + 1;
}

function fmtDate(v) {
  if (!v) return "";
  let s = v;
  if (typeof s === "string" && !s.endsWith("Z") && !s.includes("+")) s += "Z";
  const d = new Date(s);
  if (isNaN(d)) return v;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

function dueDateClass(v, status) {
  if (!v || status === "Completed" || status === "Cancelled" || status === "NA") return "";
  const parts = (v.split("T")[0]).split("-");
  const due   = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (due < today)                        return " mv2-date--overdue";
  if (due.getTime() === today.getTime())  return " mv2-date--today";
  return " mv2-date--future";
}

function toInputDate(v) {
  if (!v) return "";
  let s = v;
  if (typeof s === "string" && !s.endsWith("Z") && !s.includes("+")) s += "Z";
  const d = new Date(s);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isAdminInternal(user) {
  return ["Admin", "Super Admin"].includes(user?.db_role) && user?.db_user_type === "Internal";
}

// Admin, Super Admin, AM, or Team Lead only — mirrors the backend gate on
// POST /tasks/multi-project (_can_create_multi_project_tasks in app/main.py).
function canAssignMultipleProjects(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  const userType = String(user?.db_user_type || "").trim().toLowerCase();
  if (["admin", "super admin", "am"].includes(role) && ["internal", ""].includes(userType)) return true;
  return user?.is_team_lead === true;
}

function normalizeTaskVisibility(rawVisibility) {
  const value = String(rawVisibility || "").trim().toLowerCase();
  if (value === "private") return "Private";
  if (value === "internal") return "Internal";
  return "Public";
}

function getCurrentUserUuid(user) {
  return String(user?.db_user_id || "").trim().toLowerCase();
}

function isExternalUser(user) {
  const t = String(user?.db_user_type || "").trim().toLowerCase();
  return t === "external" || t === "external user";
}

export function canViewTaskByVisibility(task, user) {
  const visibility = normalizeTaskVisibility(task?.visibility);
  if (visibility === "Public") return true;
  if (visibility === "Internal") return !isExternalUser(user);
  const currentUserId = getCurrentUserUuid(user);
  if (!currentUserId) return false;
  return String(task?.created_by || "").trim().toLowerCase() === currentUserId;
}

function normalizeTaskRecord(task) {
  return { ...(task || {}), visibility: normalizeTaskVisibility(task?.visibility) };
}

const DRAWER_IMPL_STATUS_OPTIONS = IMPL_STATUS_OPTIONS.filter((status) => status !== "NA");

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);



const IconDownload = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconTable = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="3" y1="15" x2="21" y2="15"/>
    <line x1="9" y1="9" x2="9" y2="21"/>
  </svg>
);

const IconKanban = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="5" height="18" rx="1"/>
    <rect x="10" y="3" width="5" height="12" rx="1"/>
    <rect x="17" y="3" width="5" height="15" rx="1"/>
  </svg>
);

const IconChevronUp = () => (
  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEyeOff = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.86 10.86 0 0 1 12 19C5 19 1 12 1 12a21.77 21.77 0 0 1 5.06-6.94"/>
    <path d="M9.9 4.24A10.93 10.93 0 0 1 12 4c7 0 11 8 11 8a21.82 21.82 0 0 1-2.16 3.19"/>
    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// ─── Empty task form ──────────────────────────────────────────────────────────

function makeEmpty(currentUser) {
  const defaultVisibility = isExternalUser(currentUser) ? "Public" : "Internal";
  const defaultReportingChannel = isExternalUser(currentUser) ? "External QA" : "Internal QA";
  return {
    title: "", customer: "", customer_id: "", project: "", project_id: "", epic_id: "", feature: "", feature_id: "", sub_feature: "", sub_feature_id: "",
    description: "", tmm_team_id: "", tmm_team_name: "",
    tmm_member: "", customer_due_date: "", new_enhancement: "New",
    visibility: defaultVisibility, am_name: "", status: "Open",
    source: "", reporting_channel: defaultReportingChannel,
    multiProjectMode: false,
    estimated_effort: "", // "H:MM" — intentionally not prefilled, mandatory on create
  };
}

// "H:MM" (e.g. "2:30") -> { hours, minutes, totalMinutes }, or null if malformed.
// Total is capped at 16:00 by the caller (validate()), not here, so the error
// message can distinguish "bad format" from "too long".
function parseEstimatedEffort(raw) {
  const v = String(raw || "").trim();
  const m = v.match(/^(\d{1,2}):([0-5]\d)$/);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  return { hours, minutes, totalMinutes: hours * 60 + minutes };
}

// ─── Status Dot component ─────────────────────────────────────────────────────

function StatusDot({ status, size = 7 }) {
  const color = STATUS_DOT[status] || "#AFB2BA";
  return (
    <span
      className="mv2-status-dot"
      style={{ background: color, width: size, height: size }}
    />
  );
}

// ─── Status Pill component ────────────────────────────────────────────────────

function StatusPill({ status }) {
  if (!status) return <span className="mv2-stpill st-na">—</span>;
  return (
    <span className={STATUS_PILL_CLASS[status] || "mv2-stpill st-na"}>
      {status}
    </span>
  );
}

// ─── Type Pill component ──────────────────────────────────────────────────────

function TypePill({ type }) {
  if (!type) return null;
  const cls =
    type === "Enhancement" ? "mv2-type-pill mv2-type-pill--enhancement"
    : "mv2-type-pill mv2-type-pill--new";
  return <span className={cls}>{type}</span>;
}

// ─── ComboBox — searchable input-select ──────────────────────────────────────

function ComboBox({ options = [], value, onChange, placeholder = "— select —", className = "", error = false, disabled = false }) {
  const [query,       setQuery]       = useState("");
  const [open,        setOpen]        = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  // Reset highlight when filtered list changes
  useEffect(() => { setHighlighted(0); }, [filtered.length]);

  // Focus input when dropdown opens so user can type immediately
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlighted];
    item?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const openDropdown  = () => { setQuery(""); setOpen(true); };
  const closeDropdown = () => { setQuery(""); setOpen(false); };

  const commit = (opt) => {
    if (disabled) return;
    onChange(opt.value);
    closeDropdown();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { openDropdown(); return; }
      // Printable character while closed: open and seed the query with it
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        setOpen(true);
        setQuery(e.key);
        return;
      }
      return;
    }
    if (e.key === "ArrowDown")  { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")    { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter")      { e.preventDefault(); if (filtered[highlighted]) commit(filtered[highlighted]); }
    if (e.key === "Escape")     { closeDropdown(); }
    if (e.key === "Tab")        { closeDropdown(); }
  };

  return (
    <div ref={wrapRef} className={`mv2-combobox${open ? " mv2-combobox--open" : ""}${error ? " mv2-combobox--error" : ""}${disabled ? " mv2-combobox--disabled" : ""}${className ? ` ${className}` : ""}`}>
      <div className="mv2-combobox-input-wrap">
        <input
          ref={inputRef}
          className="mv2-combobox-input v2drw-control"
          value={open ? query : (selected?.label || "")}
          placeholder={open ? "Type to filter…" : (selected?.label ? "" : placeholder)}
          disabled={disabled}
          readOnly={!open}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (!open && !disabled) openDropdown(); }}
          onMouseDown={(e) => { if (!open && !disabled) { e.preventDefault(); openDropdown(); } }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <span className="mv2-combobox-arrow" onMouseDown={(e) => { if (disabled) return; e.preventDefault(); open ? closeDropdown() : openDropdown(); }}>
          <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="1 1 5 5 9 1"/>
          </svg>
        </span>
      </div>
      {open && (
        <ul ref={listRef} className="mv2-combobox-list">
          {filtered.length === 0 ? (
            <li className="mv2-combobox-empty">No matches</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={`${opt.value}__${i}`}
                className={`mv2-combobox-option${i === highlighted ? " mv2-combobox-option--highlighted" : ""}${opt.value === value ? " mv2-combobox-option--selected" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Drawer form ──────────────────────────────────────────────────────────────

// ─── Activity feed ───────────────────────────────────────────────────────────

const FIELD_LABELS = {
  title:             "Title",
  customer:          "Customer",
  project:           "Project",
  feature:           "Feature",
  sub_feature:       "Sub-feature",
  description:       "Description",
  status:            "Status",
  new_enhancement:   "Type",
  visibility:        "Visibility",
  am_name:           "AM Name",
  customer_due_date: "Customer Due Date",
  tmm_member:        "Assignee",
  tmm_team_id:       "Team",
  tmm_team_name:     "Team Name",
  attachment_upload: "Attachment",
  attachment_delete: "Attachment",
};

// Only these fields are meaningful to diff — avoids phantom changes from
// DB fields that don't exist in the API payload and vice-versa.
const TRACKED_FIELDS = [
  "customer", "project", "feature", "sub_feature", "description",
  "status", "new_enhancement", "visibility", "am_name", "customer_due_date",
  "tmm_member", "tmm_team_name",
];

function parseJson(s) {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}

// Normalize a value so null / undefined / "" / "null" / "None" all compare equal.
function norm(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (s === "null" || s === "None") return "";
  // Normalise ISO date strings to just the date part so timezone noise doesn't
  // cause phantom diffs (e.g. "2026-04-27T00:00:00+00:00" → "2026-04-27")
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return s;
}

function diffObjects(oldObj, newObj) {
  if (!oldObj || !newObj) return [];
  const changes = [];
  for (const key of TRACKED_FIELDS) {
    const ov = norm(oldObj[key]);
    const nv = norm(newObj[key]);
    if (ov !== nv) {
      changes.push({ field: key, from: ov, to: nv });
    }
  }
  return changes;
}

function fmtActivityDate(v) {
  if (!v) return "";
  const d = new Date(typeof v === "string" && !v.includes("Z") && !v.includes("+") ? v + "Z" : v);
  if (isNaN(d)) return v;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ActivityFeed({ entries, loading }) {
  if (loading) return <div className="mv2-act-state">Loading…</div>;
  if (!entries) return null;
  if (entries.length === 0) return <div className="mv2-act-state">No activity recorded yet.</div>;

  return (
    <div className="mv2-act-feed">
      {entries.map((entry) => {
        const isAttachmentEvent =
          entry.column_key === "attachment_upload" || entry.column_key === "attachment_delete";
        const isFullRecord = entry.column_key === "full_record";
        const oldObj = isFullRecord ? parseJson(entry.old_value) : null;
        const newObj = isFullRecord ? parseJson(entry.new_value) : null;
        const diffs  = isFullRecord ? diffObjects(oldObj, newObj) : [];
        const isCreate = isFullRecord && !oldObj;
        const attachmentObj = isAttachmentEvent
          ? (parseJson(entry.new_value) || parseJson(entry.old_value) || {})
          : null;
        const attachmentAction = entry.column_key === "attachment_delete" ? "Deleted" : "Uploaded";
        const attachmentName = attachmentObj?.file_name || "Attachment";

        return (
          <div key={entry.id} className="mv2-act-entry">
            <div className="mv2-act-dot" />
            <div className="mv2-act-content">
              <div className="mv2-act-header">
                <span className="mv2-act-user">{entry.changed_by || "System"}</span>
                <span className="mv2-act-time">{fmtActivityDate(entry.changed_at)}</span>
              </div>

              {isCreate ? (
                <div className="mv2-act-create">
                  <span className="mv2-act-create-badge">Created</span>
                  {newObj?.description && (
                    <span className="mv2-act-create-desc">"{newObj.description}"</span>
                  )}
                </div>
              ) : isAttachmentEvent ? (
                <div className="mv2-act-create">
                  <span className="mv2-act-create-badge">{attachmentAction}</span>
                  <span className="mv2-act-create-desc">"{attachmentName}"</span>
                </div>
              ) : isFullRecord && diffs.length > 0 ? (
                <div className="mv2-act-changes">
                  {diffs.map(({ field, from, to }) => (
                    <div key={field} className="mv2-act-change">
                      <span className="mv2-act-field">{FIELD_LABELS[field] || field}</span>
                      <span className={`mv2-act-from${!from ? " mv2-act-from--empty" : ""}`}>
                        {from || "—"}
                      </span>
                      <span className="mv2-act-arrow">→</span>
                      <span className="mv2-act-to">{to || <em>cleared</em>}</span>
                    </div>
                  ))}
                </div>
              ) : isFullRecord && diffs.length === 0 ? (
                <div className="mv2-act-label">Saved — no tracked fields changed</div>
              ) : (
                <div className="mv2-act-changes">
                  <div className="mv2-act-change">
                    <span className="mv2-act-field">{FIELD_LABELS[entry.column_key] || entry.column_key}</span>
                    <span className={`mv2-act-from${!entry.old_value ? " mv2-act-from--empty" : ""}`}>
                      {entry.old_value || "—"}
                    </span>
                    <span className="mv2-act-arrow">→</span>
                    <span className="mv2-act-to">{entry.new_value || <em>cleared</em>}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Edit timesheet entry modal ───────────────────────────────────────────────

function validateDrawerTimeEntryLimit(hours, minutes) {
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return { ok: false, msg: "Enter a valid time value", level: "warn" };
  if (h < 0 || h > 24) return { ok: false, msg: "Hours must be between 0 and 24", level: "warn" };
  if (m < 0 || m > 59) return { ok: false, msg: "Minutes must be between 0 and 59", level: "warn" };

  const totalMinutes = h * 60 + m;
  if (totalMinutes > 240) {
    return {
      ok: false,
      kind: "hard_limit",
      msg: "Only upto 4 hours, time log is allowed. Split the entry into two or more, if you want to enter more than 4 hours",
      level: "warn",
    };
  }
  if (totalMinutes > 120) {
    return {
      ok: false,
      kind: "confirm_over_2h",
      msg: "You are entering more than 2 hours, check if it is valid",
      level: "warn",
    };
  }
  return { ok: true };
}

function TsEditModal({ entry, showToast, onClose, onSaved }) {
  const [form, setForm] = useState({
    report_date:   entry.report_date || "",
    title:         entry.title || "",
    overall_notes: entry.overall_notes || "",
    hours_spent:   entry.hours_spent ?? 0,
    minutes_spent: entry.minutes_spent ?? 0,
    status:        entry.status || "Draft",
  });
  const [saving, setSaving] = useState(false);
  const [alertDialog, setAlertDialog] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const closeAlert = () => setAlertDialog(null);

  const openAlert = (type, message, confirmLabel = null, onConfirm = null) => {
    setAlertDialog({ type, message, confirmLabel, onConfirm });
  };

  const submitUpdate = async () => {
    const h = Number(form.hours_spent);
    const m = Number(form.minutes_spent);
    setSaving(true);
    try {
      const res = await fetch(`${API}/daily-reports/${entry.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, hours_spent: h, minutes_spent: m }),
      });
      if (!res.ok) {
        let detail = "Failed to update entry";
        try {
          const err = await res.json();
          detail = err?.detail || detail;
        } catch {
          // keep fallback
        }
        throw new Error(detail);
      }
      showToast("Entry updated");
      await onSaved();
      onClose();
    } catch (err) {
      openAlert("error", err?.message || "Failed to update entry");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { openAlert("warning", "Title is required"); return; }
    const h = Number(form.hours_spent);
    const m = Number(form.minutes_spent);
    const guard = validateDrawerTimeEntryLimit(h, m);
    if (!guard.ok) {
      if (guard.kind === "confirm_over_2h") {
        openAlert("warning", guard.msg, "Submit", () => submitUpdate());
        return;
      }
      if (guard.msg) openAlert("warning", guard.msg);
      return;
    }
    submitUpdate();
  };

  return createPortal(
    <>
      <div className="dts-overlay" onClick={onClose}>
        <div className="dts-modal" onClick={(e) => e.stopPropagation()}>
          <div className="dts-modal-header">
            <span className="dts-modal-title">Edit Time Entry</span>
            <button className="dts-close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="dts-modal-body">
            <div className="dts-field">
              <label className="dts-label">Date</label>
              <input type="date" className="dts-input" value={form.report_date}
                onChange={(e) => set("report_date", e.target.value)} />
            </div>
            <div className="dts-field">
              <label className="dts-label">Title *</label>
              <input className="dts-input" autoFocus value={form.title}
                placeholder="e.g. Sprint 12 Day 3"
                onChange={(e) => set("title", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }} />
            </div>
            <div className="dts-field">
              <label className="dts-label">Overall Notes</label>
              <textarea className="dts-textarea" rows={3} value={form.overall_notes}
                placeholder="Summary of work done…"
                onChange={(e) => set("overall_notes", e.target.value)} />
            </div>
            <div className="dts-modal-row">
              <div className="dts-field">
                <label className="dts-label">Hours Spent</label>
                <input type="number" className="dts-input" min={0} max={24}
                  value={form.hours_spent}
                  onChange={(e) => set("hours_spent", e.target.value)} />
              </div>
              <div className="dts-field">
                <label className="dts-label">Minutes Spent</label>
                <input type="number" className="dts-input" min={0} max={59}
                  value={form.minutes_spent}
                  onChange={(e) => set("minutes_spent", e.target.value)} />
              </div>
            </div>
            <div className="dts-field">
              <label className="dts-label">Status</label>
              <select className="dts-select" value={form.status}
                onChange={(e) => set("status", e.target.value)}>
                {["Draft", "Submitted", "Approved"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="dts-modal-footer">
            <button className="uv-btn uv-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button className="uv-btn uv-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      {alertDialog && (
        <div className="dts-alert-overlay" onClick={closeAlert}>
          <div className="dts-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dts-alert-icon" aria-hidden="true">⚠</div>
            <div className="dts-alert-title">
              {alertDialog.type === "error" ? "Error" : "Warning"}
            </div>
            <div className="dts-alert-message">{alertDialog.message}</div>
            <div className="dts-alert-actions">
              {alertDialog.confirmLabel && (
                <button
                  className="uv-btn uv-btn--save"
                  onClick={() => {
                    const fn = alertDialog.onConfirm;
                    closeAlert();
                    fn?.();
                  }}
                >
                  {alertDialog.confirmLabel}
                </button>
              )}
              <button className="uv-btn uv-btn--cancel" onClick={closeAlert}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

// ─── Timesheet entries table (inside Efforts tab) ────────────────────────────

function TsEntriesTable({ entries, showToast, onRefresh, isAdmin }) {
  const [notesPopup, setNotesPopup] = useState(null); // { id, notes, anchorRect }
  const [editEntry,  setEditEntry]  = useState(null);
  const popupRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    if (!notesPopup) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setNotesPopup(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notesPopup]);

  const openNotes = (e, entry) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setNotesPopup({ id: entry.id, notes: entry.overall_notes, anchorRect: rect });
  };

  return (
    <>
      <table className="rd-ts-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Title</th>
            <th>Time</th>
            <th>Logged By</th>
            <th>Status</th>
            <th style={{ width: 56 }} />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const isDraft = (e.status || "").toLowerCase() === "draft";
            const canEdit = isDraft || isAdmin;
            return (
              <tr key={e.id}>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(e.report_date + "T00:00:00Z")}</td>
                <td>
                  <span className="rd-ts-title" title={e.title}>{e.title || "—"}</span>
                </td>
                <td className="rd-ts-time" style={{ whiteSpace: "nowrap" }}>
                  {(e.hours_spent || 0) > 0 || (e.minutes_spent || 0) > 0
                    ? `${e.hours_spent || 0}h ${e.minutes_spent || 0}m`
                    : "—"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>{e.user_display_name || "—"}</td>
                <td>
                  <span className={`rd-ts-badge rd-ts-badge--${(e.status || "draft").toLowerCase().replace(/\s/g, "-")}`}>
                    {e.status || "Draft"}
                  </span>
                </td>
                <td className="rd-ts-actions-cell">
                  {canEdit && (
                    <button
                      className="rd-ts-notes-btn"
                      title="Edit entry"
                      onClick={() => setEditEntry(e)}
                      type="button"
                    >
                      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 2l3 3-8 8H3v-3L11 2z"/>
                      </svg>
                    </button>
                  )}
                  {e.overall_notes && (
                    <button
                      className="rd-ts-notes-btn"
                      title="View notes"
                      onClick={(ev) => openNotes(ev, e)}
                      type="button"
                    >
                      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="12" height="12" rx="2"/>
                        <line x1="5" y1="6" x2="11" y2="6"/>
                        <line x1="5" y1="9" x2="9" y2="9"/>
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Notes popup */}
      {notesPopup && createPortal(
        <div
          ref={popupRef}
          className="rd-ts-notes-popup"
          style={{
            position: "fixed",
            top: notesPopup.anchorRect.bottom + 6,
            left: Math.min(notesPopup.anchorRect.left, window.innerWidth - 280),
            zIndex: 9999,
          }}
        >
          <div className="rd-ts-notes-popup-title">Notes</div>
          <div className="rd-ts-notes-popup-body">{notesPopup.notes}</div>
        </div>,
        document.body
      )}

      {/* Edit entry modal */}
      {editEntry && (
        <TsEditModal
          entry={editEntry}
          showToast={showToast}
          onClose={() => setEditEntry(null)}
          onSaved={onRefresh}
        />
      )}
    </>
  );
}

// ─── Drawer form ──────────────────────────────────────────────────────────────

function TaskDrawer({ task, customers, users, features, user, onClose, onSave, onSaveMultiProject, onDelete, showToast }) {
  const isEdit = !!task?.id;
  const isAdmin = ["admin", "super admin"].includes(String(user?.db_role || "").toLowerCase());
  const [drawerTab, setDrawerTab] = useState("overview");
  const [form, setForm] = useState(
    isEdit
      ? {
          ...task,
          customer_due_date: toInputDate(task.customer_due_date),
          am_name:           task.am_name           ?? "",
          description:       task.description       ?? "",
          title:             task.title             ?? "",
          customer:          task.customer          ?? "",
          customer_id:       task.customer_id       ?? "",
          project:           task.project           ?? "",
          project_id:        task.project_id        ?? "",
          epic_id:           task.epic_id           ?? "",
          feature:           task.feature           ?? "",
          feature_id:        task.feature_id        ?? "",
          sub_feature:       task.sub_feature       ?? "",
          sub_feature_id:    task.sub_feature_id    ?? "",
          tmm_member:        task.tmm_member        ?? "",
          tmm_team_name:     task.tmm_team_name     ?? "",
          visibility:        normalizeTaskVisibility(task.visibility),
          source:            task.source            ?? "",
          reporting_channel: task.reporting_channel ?? "",
        }
      : makeEmpty(user)
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingTagIds, setPendingTagIds] = useState([]);
  const [assigneeTeamMap, setAssigneeTeamMap] = useState({});
  const [epicOptions, setEpicOptions] = useState([]);
  const [epicLoading, setEpicLoading] = useState(false);
  const [dueDateModal, setDueDateModal] = useState(null); // { fromDate, toDate, pendingPayload, pendingTagIds }
  const [showMultiProjectModal, setShowMultiProjectModal] = useState(false);
  const originalDueDate = isEdit ? toInputDate(task.customer_due_date) : null;

  // ── Attachments state ──────────────────────────────────────────────────────
  const [attachments,   setAttachments]   = useState(null);
  const [attLoading,    setAttLoading]    = useState(false);
  const [showAttModal,  setShowAttModal]  = useState(false);
  const [confirmDelAtt, setConfirmDelAtt] = useState(null);
  const [attDeleting,   setAttDeleting]   = useState(null);

  // ── Efforts (timesheet) state ──────────────────────────────────────────────
  const [tsEntries,  setTsEntries]  = useState(null);
  const [tsLoading,  setTsLoading]  = useState(false);
  const [showTsModal, setShowTsModal] = useState(false);

  // ── Activity state ─────────────────────────────────────────────────────────
  const [activity,        setActivity]        = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // ── Related Defects state ──────────────────────────────────────────────────
  const [relatedDefects,        setRelatedDefects]        = useState(null);
  const [relatedDefectsLoading, setRelatedDefectsLoading] = useState(false);
  const [quickViewDefectId,     setQuickViewDefectId]     = useState(null);
  const loggedInUserName = String(user?.preferred_username || user?.upn || user?.email || "").trim();
  const isPrivateVisibility = normalizeTaskVisibility(form.visibility) === "Private";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ── Tab data loaders ───────────────────────────────────────────────────────

  const loadAttachments = useCallback(async () => {
    if (!task?.id) return;
    setAttLoading(true);
    try {
      const res = await fetch(`${API}/attachments/task/${task.id}`, { credentials: "include" });
      setAttachments(res.ok ? await res.json() : []);
    } catch { setAttachments([]); }
    finally { setAttLoading(false); }
  }, [task?.id]);

  const loadTsEntries = useCallback(async () => {
    if (!task?.id) return;
    setTsLoading(true);
    try {
      const res = await fetch(`${API}/daily-reports?ref_type=task&ref_id=${task.id}`, { credentials: "include" });
      setTsEntries(res.ok ? await res.json() : []);
    } catch { setTsEntries([]); }
    finally { setTsLoading(false); }
  }, [task?.id]);

  const handleAttDelete = async () => {
    if (!confirmDelAtt) return;
    setAttDeleting(confirmDelAtt.id);
    setConfirmDelAtt(null);
    try {
      await fetch(`${API}/attachments/${confirmDelAtt.id}`, { method: "DELETE", credentials: "include" });
      setAttachments((prev) => prev.filter((a) => a.id !== confirmDelAtt.id));
    } catch { /* leave list as-is */ }
    finally { setAttDeleting(null); }
  };

  const loadActivity = useCallback(async () => {
    if (!task?.id) return;
    setActivityLoading(true);
    try {
      const res = await fetch(`${API}/audit-logs/tasks/${task.id}`, { credentials: "include" });
      setActivity(res.ok ? await res.json() : []);
    } catch { setActivity([]); }
    finally { setActivityLoading(false); }
  }, [task?.id]);

  const loadRelatedDefects = useCallback(async () => {
    if (!task?.id) return;
    setRelatedDefectsLoading(true);
    try {
      const res = await fetch(`${API}/defects/by-ref/task/${task.id}`, { credentials: "include" });
      setRelatedDefects(res.ok ? await res.json() : []);
    } catch { setRelatedDefects([]); }
    finally { setRelatedDefectsLoading(false); }
  }, [task?.id]);

  const switchDrawerTab = (tab) => {
    setDrawerTab(tab);
    if (tab === "attachments" && attachments === null && isEdit) loadAttachments();
    if (tab === "efforts"     && tsEntries  === null && isEdit) loadTsEntries();
    if (tab === "activity"    && activity   === null && isEdit) loadActivity();
    if (tab === "defects"     && relatedDefects === null && isEdit) loadRelatedDefects();
  };

  // Auto-switch to Discussions tab on open if the task already has comments
  useEffect(() => {
    if (!isEdit || !task?.id) return;
    let cancelled = false;
    fetch(`${API}/discussions/feature/${task.id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        if (list.length > 0) {
          setDrawerTab("discussions");
        }
      })
      .catch(() => {});                  // silent — tab stays on overview if fetch fails
    return () => { cancelled = true; };
  }, [isEdit, task?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveAssigneeTeam = useCallback(async (assigneeUserName) => {
    const assignee = String(assigneeUserName || "").trim();
    if (!assignee) return null;

    if (assigneeTeamMap[assignee]) return assigneeTeamMap[assignee];

    const userRow = users.find((u) => u.user_name === assignee);
    if (!userRow?.id) return null;

    try {
      const res = await fetch(`${API}/users/${userRow.id}/teams`, { credentials: "include" });
      if (!res.ok) return null;
      const teamsForUser = await res.json();
      const primary = Array.isArray(teamsForUser) ? teamsForUser[0] : null;
      if (!primary?.id) return null;
      const resolved = { id: primary.id, team_name: primary.team_name || "" };
      setAssigneeTeamMap((prev) => ({ ...prev, [assignee]: resolved }));
      return resolved;
    } catch {
      return null;
    }
  }, [assigneeTeamMap, users]);

  useEffect(() => {
    if (!isPrivateVisibility || !loggedInUserName) return;
    let cancelled = false;
    const syncPrivateAssignee = async () => {
      const resolvedTeam = await resolveAssigneeTeam(loggedInUserName);
      if (cancelled) return;
      setForm((prev) => ({
        ...prev,
        tmm_member: loggedInUserName,
        tmm_team_id: resolvedTeam?.id || prev.tmm_team_id || "",
        tmm_team_name: resolvedTeam?.team_name || prev.tmm_team_name || "",
      }));
      setErrors((prev) => ({ ...prev, tmm_member: "" }));
    };
    syncPrivateAssignee();
    return () => { cancelled = true; };
  }, [isPrivateVisibility, loggedInUserName, resolveAssigneeTeam]);

  const validate = () => {
    const e = {};
    if (!form.title?.trim())          e.title            = "Required";
    if (!form.description?.trim())    e.description      = "Required";
    if (!form.customer_due_date?.trim()) e.customer_due_date = "Required";
    // Estimated effort: create-only (edited afterward via the Efforts tab),
    // shared across single- and multi-project mode, same as title/description.
    if (!isEdit) {
      if (!form.estimated_effort?.trim()) {
        e.estimated_effort = "Required";
      } else {
        const parsed = parseEstimatedEffort(form.estimated_effort);
        if (!parsed) e.estimated_effort = "Enter as H:MM, e.g. 2:30";
        else if (parsed.totalMinutes > 16 * 60) e.estimated_effort = "Cannot exceed 16:00";
      }
    }
    if (!form.multiProjectMode) {
      if (!form.project_id?.trim())    e.project          = "Required";
      if (!form.epic_id?.trim())       e.epic_id          = "Required";
      if (!form.am_name?.trim())       e.am_name          = "Required";
      if (isPrivateVisibility && !loggedInUserName) e.tmm_member = "Unable to resolve logged-in user";
      if (!form.tmm_member?.trim())     e.tmm_member       = "Required";
      if (isPrivateVisibility && loggedInUserName && form.tmm_member !== loggedInUserName) {
        e.tmm_member = "Private tasks must be assigned to yourself";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Title/description/due date are shared across every task the multi-project
  // popup creates, so they must be filled in before it opens — the popup only
  // owns project/epic/assignee. Reuses validate(), which skips the
  // single-project-only checks (project/epic/AM/assignee) while multiProjectMode
  // is true, so this only surfaces the fields the popup actually needs.
  const openMultiProjectModal = () => {
    if (!validate()) {
      showToast?.("Fill in the required fields above first", "error");
      return;
    }
    setShowMultiProjectModal(true);
  };

  const handleMultiProjectSubmit = async (payload) => {
    await onSaveMultiProject(payload);
  };

  const handleSave = async () => {
    if (!validate()) return;

    const effectiveAssignee = isPrivateVisibility ? loggedInUserName : form.tmm_member;
    const resolvedTeam = await resolveAssigneeTeam(effectiveAssignee);
    if (!resolvedTeam?.id) {
      setErrors((prev) => ({ ...prev, tmm_member: "Unable to resolve team for selected assignee" }));
      showToast?.("Selected assignee has no team mapping", "error");
      return;
    }

    // eslint-disable-next-line no-unused-vars
    const { tag_names: _tag_names, estimated_effort: _estimated_effort, ...formWithoutTags } = form;
    const parsedEffort = !isEdit ? parseEstimatedEffort(form.estimated_effort) : null;
    const payload = {
      ...formWithoutTags,
      visibility: normalizeTaskVisibility(form.visibility),
      tmm_member: effectiveAssignee,
      tmm_team_id: resolvedTeam.id,
      tmm_team_name: resolvedTeam.team_name || form.tmm_team_name || "",
      ...(parsedEffort ? {
        effort_estimated_hours: parsedEffort.hours,
        effort_estimated_minutes: parsedEffort.minutes,
      } : {}),
    };

    // Intercept if due date changed on an existing task
    if (isEdit && originalDueDate !== form.customer_due_date) {
      setDueDateModal({
        fromDate: originalDueDate,
        toDate: form.customer_due_date,
        pendingPayload: payload,
        pendingTagIds: isEdit ? [] : pendingTagIds,
      });
      return;
    }

    setSaving(true);
    await onSave(payload, isEdit ? [] : pendingTagIds);
    if (!isEdit) setPendingTagIds([]);
    setSaving(false);
  };

  const handleDueDateConfirm = async (reason) => {
    const { pendingPayload, pendingTagIds: ptids } = dueDateModal;
    setDueDateModal(null);
    setSaving(true);
    await onSave({ ...pendingPayload, due_date_change_reason: reason }, ptids);
    setSaving(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  // Projects from accessible customer/project hierarchy
  const projectOptions = useMemo(() => {
    const options = [];
    (customers || []).forEach((c) => {
      const customerId = c.id || c.customer_id || "";
      (c.projects || []).forEach((p) => {
        const projectId = p.id || p.project_id || p.project_uuid || "";
        options.push({
          id: projectId,
          project_name: p.project_name,
          customer_name: c.customer_name,
          customer_id: customerId,
          am_name: p.am_name || c.am_name || "",
        });
      });
    });
    // When editing, the task's project may be inactive/closed and absent from the
    // active-only list. Inject it so the ComboBox always renders the current value.
    if (isEdit && task?.project && !options.find((p) => p.project_name === task.project)) {
      options.unshift({
        id:            task.project_id    || "",
        project_name:  task.project,
        customer_name: task.customer      || "",
        customer_id:   task.customer_id   || "",
        am_name:       task.am_name       || "",
      });
    }
    return options;
  }, [customers, isEdit, task]);

  useEffect(() => {
    if (form.project_id || !form.project) return;
    const proj = projectOptions.find((p) => p.project_name === form.project);
    if (!proj?.id) return;
    setForm((prev) => ({ ...prev, project_id: proj.id }));
  }, [form.project_id, form.project, projectOptions]);

  useEffect(() => {
    const projectId = String(form.project_id || "").trim();
    if (!projectId) {
      setEpicOptions([]);
      return;
    }
    let cancelled = false;
    const loadEpics = async () => {
      setEpicLoading(true);
      try {
        const res = await fetch(
          `${API}/epics?project_id=${encodeURIComponent(projectId)}&include_closed=${isEdit ? "true" : "false"}`,
          { credentials: "include" }
        );
        if (!res.ok) {
          let detail = "Failed to load epics";
          try {
            const body = await res.json();
            detail = body?.detail || detail;
          } catch {
            // ignore parse failures
          }
          throw new Error(detail);
        }
        const data = await res.json();
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : []).map((e) => ({
          id: e.id,
          epic_code: e.epic_code || "",
          epic_name: e.epic_name || "",
          status: e.status || "",
        }));
        setEpicOptions(list);
      } catch (err) {
        if (!cancelled) {
          setEpicOptions([]);
          showToast?.(err?.message || "Failed to load epics", "error");
        }
      } finally {
        if (!cancelled) setEpicLoading(false);
      }
    };
    loadEpics();
    return () => { cancelled = true; };
  }, [form.project_id, showToast]);

  useEffect(() => {
    if (!form.epic_id) return;
    if (epicOptions.length === 0) return;
    if (epicOptions.some((e) => e.id === form.epic_id)) return;
    setForm((prev) => ({ ...prev, epic_id: "" }));
  }, [epicOptions, form.epic_id]);

  // Sub-features for selected feature — prefer ID lookup (exact), fall back to
  // name lookup for tasks loaded from the server where feature_id isn't stored.
  const subFeatureOptions = useMemo(() => {
    const feat = (features || []).find((f) => f.id === form.feature_id)
              || (features || []).find((f) => f.feature_name === form.feature);
    return (feat?.sub_features || []).map((sf) => ({
      value: sf.sub_feature_name,
      label: sf.sub_feature_name,
    }));
  }, [features, form.feature_id, form.feature]);
  const externalUser = isExternalUser(user);
  const drawerTabs = isEdit
    ? [
        { key: "overview", label: "Overview", icon: "overview" },
        { key: "discussions", label: "Discussions", icon: "discussions" },
        { key: "attachments", label: "Attachments", icon: "attachments" },
        ...(externalUser ? [] : [{ key: "efforts", label: "Time Entry", icon: "time" }]),
        { key: "defects", label: "Related Defects", icon: "defects" },
        { key: "activity", label: "History", icon: "history" },
      ]
    : [{ key: "overview", label: "Overview", icon: "overview" }];

  useEffect(() => {
    if (externalUser && drawerTab === "efforts") {
      setDrawerTab("overview");
    }
  }, [externalUser, drawerTab]);

  return (
    <>
      <div className="v2drw-scrim" />
      <div className="v2drw" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            {isEdit && (
              <div className="mv2-drawer-task-id">
                {task.task_code || `#${task.id?.toString().slice(0, 8)}`}
              </div>
            )}
            <div className="v2drw-title">
              {isEdit ? (form.title || form.description || "Edit task") : "New task"}
            </div>
          </div>
          <button className="v2drw-close" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {/* Drawer tabs */}
        <DrawerTabs tabs={drawerTabs} activeTab={drawerTab} onChange={switchDrawerTab} />

        <div className="v2drw-body mv2-drawer-body-content">
          {drawerTab === "overview" && (
          <div className="mv2-form-grid">
            {/* Title */}
            <div className="mv2-form-field mv2-form-field--full">
              <label className="mv2-form-label mv2-form-label--required v2drw-form-label">Title</label>
              <input
                type="text"
                className={`mv2-form-input v2drw-control${errors.title ? " mv2-input--error" : ""}`}
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Short task title"
                maxLength={200}
                autoFocus
              />
              {errors.title && <span className="mv2-form-error">{errors.title}</span>}
            </div>

            {/* Description */}
            <div className="mv2-form-field mv2-form-field--full">
              <div className="mv2-form-label-row">
                <label className="mv2-form-label mv2-form-label--required v2drw-form-label">Description</label>
                {form.title?.trim() && (
                  <button
                    type="button"
                    className="mv2-form-label-link"
                    onClick={() => set("description", form.title)}
                  >
                    Copy title to description
                  </button>
                )}
              </div>
              <textarea
                className={`mv2-form-textarea v2drw-control${errors.description ? " mv2-input--error" : ""}`}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Additional details"
                rows={3}
              />
              {errors.description && <span className="mv2-form-error">{errors.description}</span>}
            </div>

            {/* Visibility */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Visibility</label>
              <ComboBox
                options={TASK_VISIBILITY_OPTIONS.map((option) => ({ value: option, label: option }))}
                value={normalizeTaskVisibility(form.visibility)}
                onChange={async (v) => {
                  const nextVisibility = normalizeTaskVisibility(v);
                  if (nextVisibility === "Private") {
                    const assignee = loggedInUserName;
                    const resolvedTeam = assignee ? await resolveAssigneeTeam(assignee) : null;
                    setForm((prev) => ({
                      ...prev,
                      visibility: nextVisibility,
                      tmm_member: assignee || prev.tmm_member,
                      tmm_team_id: resolvedTeam?.id || prev.tmm_team_id || "",
                      tmm_team_name: resolvedTeam?.team_name || prev.tmm_team_name || "",
                    }));
                    setErrors((prev) => ({ ...prev, tmm_member: "" }));
                    return;
                  }
                  set("visibility", nextVisibility);
                }}
                placeholder="Select visibility…"
                disabled={!isEdit && isExternalUser(user)}
              />
            </div>

            {/* Project */}
            <div className="mv2-form-field">
              <label className="mv2-form-label mv2-form-label--required v2drw-form-label">
                {form.multiProjectMode ? "Projects" : "Project"}
              </label>

              {!form.multiProjectMode ? (
                <ComboBox
                  options={projectOptions.map((p) => ({
                    value: p.project_name,
                    label: `${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ""}`,
                  }))}
                  value={form.project}
                  onChange={(v) => {
                    const proj = projectOptions.find((p) => p.project_name === v);
                    const am = proj?.am_name || "";
                    const projectChanged = v !== form.project;
                    setForm((f) => ({
                      ...f,
                      project:    v,
                      project_id: proj?.id || "",
                      // Only reset epic when the project actually changes
                      epic_id:    projectChanged ? "" : f.epic_id,
                      customer:   proj?.customer_name || "",
                      customer_id: proj?.customer_id || "",
                      am_name:    am,
                    }));
                    if (errors.project) setErrors((prev) => ({ ...prev, project: "" }));
                    if (errors.epic_id && projectChanged) setErrors((prev) => ({ ...prev, epic_id: "" }));
                  }}
                  placeholder="Search project…"
                  error={!!errors.project}
                />
              ) : (
                <button
                  type="button"
                  className="mv2-multiproject-selectbtn"
                  onClick={openMultiProjectModal}
                >
                  Select projects &amp; create…
                </button>
              )}
              {!isEdit && canAssignMultipleProjects(user) && (
                <label className="mv2-multiproject-toggle mv2-multiproject-toggle--below">
                  <input
                    type="checkbox"
                    checked={form.multiProjectMode}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((f) => ({
                        ...f,
                        multiProjectMode: checked,
                        // Switching modes — clear whichever project selection doesn't apply.
                        project: checked ? "" : f.project,
                        project_id: checked ? "" : f.project_id,
                        customer: checked ? "" : f.customer,
                        customer_id: checked ? "" : f.customer_id,
                        epic_id: "",
                      }));
                      setErrors((prev) => ({ ...prev, project: "", epic_id: "" }));
                      setShowMultiProjectModal(false);
                    }}
                  />
                  Assign to multiple projects
                </label>
              )}
              {errors.project && <span className="mv2-form-error">{errors.project}</span>}
            </div>

            {/* Epic */}
            {!form.multiProjectMode && (
              <div className="mv2-form-field">
                <label className="mv2-form-label mv2-form-label--required v2drw-form-label">Epic</label>
                <ComboBox
                  options={epicOptions.map((e) => ({
                    value: e.id,
                    label: `${e.epic_code ? `${e.epic_code} · ` : ""}${e.epic_name || "Untitled Epic"}${e.status === "Closed" ? " (Closed)" : ""}`,
                  }))}
                  value={form.epic_id || ""}
                  onChange={(v) => {
                    const picked = epicOptions.find((ep) => ep.id === v);
                    // Re-selecting the task's own closed epic is a no-op — allow it silently.
                    // Block assigning a different Closed epic.
                    if (picked?.status === "Closed" && v !== form.epic_id) {
                      showToast?.("Cannot assign a Closed epic to a task", "error");
                      return;
                    }
                    set("epic_id", v);
                    if (errors.epic_id) setErrors((prev) => ({ ...prev, epic_id: "" }));
                  }}
                  placeholder={
                    !form.project_id
                      ? "Select project first"
                      : epicLoading
                        ? "Loading epics..."
                        : "Search epic…"
                  }
                  error={!!errors.epic_id}
                  disabled={!form.project_id || epicLoading}
                />
                {errors.epic_id && <span className="mv2-form-error">{errors.epic_id}</span>}
              </div>
            )}

            {/* Feature */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Feature</label>
              <ComboBox
                options={features.map((f) => ({
                  value: f.id,
                  label: f.product_name ? `${f.feature_name} (${f.product_name})` : f.feature_name,
                }))}
                value={
                  form.feature_id ||
                  features.find((f) => f.feature_name === form.feature)?.id ||
                  ""
                }
                onChange={(id) => {
                  const feat = features.find((f) => f.id === id);
                  setForm((prev) => ({
                    ...prev,
                    feature: feat?.feature_name || "",
                    feature_id: id,
                    sub_feature: "",
                    sub_feature_id: "",
                  }));
                }}
                placeholder="Search feature…"
              />
            </div>

            {/* Sub-feature */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Sub-feature</label>
              <ComboBox
                options={subFeatureOptions}
                value={form.sub_feature || ""}
                onChange={(v) => {
                  const feat = (features || []).find((f) => f.id === form.feature_id || f.feature_name === form.feature);
                  const sf = (feat?.sub_features || []).find((s) => s.sub_feature_name === v);
                  setForm((prev) => ({ ...prev, sub_feature: v, sub_feature_id: sf?.id || "" }));
                }}
                placeholder={form.feature ? "Search sub-feature…" : "Select feature first"}
              />
            </div>

            {/* Assignee — per-project in multi-project mode (picked inside the modal) */}
            {!form.multiProjectMode && (
              <div className="mv2-form-field">
                <label className="mv2-form-label mv2-form-label--required v2drw-form-label">Assignee</label>
                <ComboBox
                  options={users.map((u) => ({ value: u.user_name, label: u.display_name || u.user_name }))}
                  value={form.tmm_member}
                  onChange={async (v) => {
                    if (isPrivateVisibility) return;
                    set("tmm_member", v);
                    if (errors.tmm_member) setErrors((prev) => ({ ...prev, tmm_member: "" }));
                    const resolvedTeam = await resolveAssigneeTeam(v);
                    if (resolvedTeam?.id) {
                      setForm((f) => ({
                        ...f,
                        tmm_member: v,
                        tmm_team_id: resolvedTeam.id,
                        tmm_team_name: resolvedTeam.team_name || "",
                      }));
                    }
                  }}
                  placeholder={isPrivateVisibility ? "Private tasks are self-assigned" : "Search assignee…"}
                  error={!!errors.tmm_member}
                  disabled={isPrivateVisibility}
                />
                {errors.tmm_member && <span className="mv2-form-error">{errors.tmm_member}</span>}
              </div>
            )}

            {/* Status — only changeable after creation; new tasks always start Open */}
            {isEdit && (
              <div className="mv2-form-field">
                <label className="mv2-form-label v2drw-form-label">Status</label>
                <ComboBox
                  options={DRAWER_IMPL_STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                  value={form.status}
                  onChange={(v) => set("status", v)}
                  placeholder="Select status…"
                />
              </div>
            )}

            {/* Type */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Type</label>
              <ComboBox
                options={TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
                value={form.new_enhancement}
                onChange={(v) => set("new_enhancement", v)}
                placeholder="Select type…"
              />
            </div>

            {/* Due Date */}
            <div className="mv2-form-field">
              <label className="mv2-form-label mv2-form-label--required v2drw-form-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Due Date
                {isEdit && task?.id && <DueDateChangeBadge entityType="task" entityId={task.id} />}
              </label>
              <input
                type="date"
                className={`mv2-form-input v2drw-control${errors.customer_due_date ? " mv2-input--error" : ""}`}
                value={form.customer_due_date}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, customer_due_date: v }));
                }}
              />
              {errors.customer_due_date && <span className="mv2-form-error">{errors.customer_due_date}</span>}
            </div>

            {/* Estimated Hours — create-only; edited afterward via the Efforts tab */}
            {!isEdit && (
              <div className="mv2-form-field">
                <label className="mv2-form-label mv2-form-label--required v2drw-form-label">Estimated Hours</label>
                <input
                  type="text"
                  className={`mv2-form-input v2drw-control${errors.estimated_effort ? " mv2-input--error" : ""}`}
                  value={form.estimated_effort}
                  onChange={(e) => set("estimated_effort", e.target.value)}
                  placeholder="0:00"
                  title="Estimated effort as H:MM, up to 16:00"
                  maxLength={5}
                />
                {errors.estimated_effort && <span className="mv2-form-error">{errors.estimated_effort}</span>}
              </div>
            )}

            {/* Source */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Source</label>
              <ComboBox
                options={SOURCE_OPTIONS.map((s) => ({ value: s, label: s }))}
                value={form.source}
                onChange={(v) => set("source", v)}
                placeholder="Select source…"
              />
            </div>

            {/* Reporting Channel */}
            <div className="mv2-form-field">
              <label className="mv2-form-label v2drw-form-label">Reporting Channel</label>
              <ComboBox
                options={REPORTING_CHANNEL_OPTIONS.map((c) => ({ value: c, label: c }))}
                value={form.reporting_channel}
                onChange={(v) => set("reporting_channel", v)}
                placeholder="Select reporting channel…"
              />
            </div>

            {/* AM Name */}
            <div className="mv2-form-field">
              <label className={`mv2-form-label v2drw-form-label${form.multiProjectMode ? "" : " mv2-form-label--required"}`}>AM Name</label>
              <ComboBox
                options={users.map((u) => ({ value: u.display_name || u.user_name, label: u.display_name || u.user_name }))}
                value={form.am_name ?? ""}
                onChange={(v) => set("am_name", v)}
                placeholder="Search account manager…"
                error={!!errors.am_name}
              />
              {errors.am_name && <span className="mv2-form-error">{errors.am_name}</span>}
            </div>

            <div className="mv2-form-field mv2-form-field--full">
              <EntityTagsField
                moduleName="tasks"
                moduleScope="tasks"
                entityId={isEdit ? task.id : undefined}
                title="Tags"
                draftTagIds={pendingTagIds}
                onDraftTagIdsChange={!isEdit ? setPendingTagIds : undefined}
              />
            </div>
          </div>
          )}
          {/* ── Attachments tab ── */}
          {drawerTab === "attachments" && (
            <div className="rd-attachments">
              <div className="rd-att-toolbar">
                <button className="rd-att-btn rd-att-btn--primary" onClick={() => setShowAttModal(true)} type="button">
                  + Attach File
                </button>
              </div>
              <div className="rd-att-list">
                {attLoading ? (
                  <div className="rd-tab-empty">Loading…</div>
                ) : !attachments || attachments.length === 0 ? (
                  <div className="rd-tab-empty">No attachments yet.</div>
                ) : (
                  attachments.map((a) => (
                    <div key={a.id} className="rd-att-card">
                      <span className="rd-att-icon">
                        {!a.file_type ? "📎" : a.file_type.startsWith("image") ? "🖼" : a.file_type.includes("pdf") ? "📄" : a.file_type.includes("sheet") || a.file_type.includes("csv") ? "📊" : a.file_type.includes("word") || a.file_type.includes("doc") ? "📝" : "📎"}
                      </span>
                      <div className="rd-att-info">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            downloadAttachment(a.id).catch((err) => showToast?.(err?.message || "Failed to download attachment", "error"));
                          }}
                          className="rd-att-name"
                          title={a.file_name}
                        >
                          {a.file_name}
                        </a>
                        <div className="rd-att-meta">
                          {a.file_type && <span className="rd-att-tag">{a.file_type.split("/").pop()}</span>}
                          {a.file_size_kb > 0 && <span className="rd-att-tag rd-att-tag--size">{a.file_size_kb < 1024 ? `${a.file_size_kb} KB` : `${(a.file_size_kb / 1024).toFixed(1)} MB`}</span>}
                          {a.caption && <span className="rd-att-tag rd-att-tag--cat">{a.caption}</span>}
                          {a.uploader_name && <span className="rd-att-by">by {a.uploader_name}</span>}
                          {a.created_at && <span className="rd-att-date">{new Date(a.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                        </div>
                      </div>
                      <button
                        className="rd-att-del"
                        onClick={() => setConfirmDelAtt({ id: a.id, file_name: a.file_name })}
                        disabled={attDeleting === a.id}
                        title="Delete attachment"
                        type="button"
                      >
                        {attDeleting === a.id ? "…" : "✕"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Efforts tab ── */}
          {drawerTab === "efforts" && (
            <div className="rd-effort-wrap">
              <div className="rd-ts-section">
                <div className="rd-ts-section-header">
                  <div className="rd-ts-section-title">Timesheet Entries</div>
                  <button className="rd-ts-log-btn" onClick={() => setShowTsModal(true)} type="button">+ Log Time</button>
                </div>
                {tsLoading && <div className="rd-ts-empty">Loading…</div>}
                {!tsLoading && tsEntries !== null && tsEntries.length === 0 && (
                  <div className="rd-ts-empty">No timesheet entries yet.</div>
                )}
                {!tsLoading && tsEntries && tsEntries.length > 0 && (
                  <TsEntriesTable entries={tsEntries} showToast={showToast} onRefresh={loadTsEntries} isAdmin={isAdminInternal(user)} />
                )}
              </div>
              <EffortEstimatePanel refType="task" refId={task.id} />
            </div>
          )}

          {/* ── Discussions tab ── */}
          {drawerTab === "discussions" && (
            <EntityDiscussionsTab entityType="feature" entityId={task?.id} showToast={showToast} />
          )}

          {/* ── Activity tab ── */}
          {drawerTab === "activity" && (
            <ActivityFeed entries={activity} loading={activityLoading} />
          )}

          {/* ── Related Defects tab ── */}
          {drawerTab === "defects" && (
            <div className="rd-attachments">
              <div className="rd-att-list">
                {relatedDefectsLoading ? (
                  <div className="rd-tab-empty">Loading…</div>
                ) : !relatedDefects || relatedDefects.length === 0 ? (
                  <div className="rd-tab-empty">No defects linked to this task.</div>
                ) : (
                  relatedDefects.map((d) => (
                    <div
                      key={d.id}
                      className="rd-att-card"
                      style={{ cursor: "pointer" }}
                      onClick={() => setQuickViewDefectId(d.id)}
                    >
                      <div className="rd-att-info">
                        <span className="rd-att-name">{d.defect_code} — {d.title}</span>
                        <div className="rd-att-meta">
                          {d.status && <span className="rd-att-tag">{d.status}</span>}
                          {d.severity && <span className="rd-att-tag rd-att-tag--cat">{d.severity}</span>}
                          {d.priority && <span className="rd-att-tag rd-att-tag--size">{d.priority}</span>}
                          {d.due_date && (
                            <span className="rd-att-date">
                              {new Date(d.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {form.multiProjectMode && drawerTab === "overview" && (
          <div className="mv2-multiproject-summary">
            Use "Select projects &amp; create…" above — tasks are created directly from that popup.
          </div>
        )}

        <div className="v2drw-foot">
          {isEdit && drawerTab === "activity" && isAdmin && (
            <button
              className="mv2-btn-delete"
              onClick={() => onDelete(task)}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <div className="v2drw-foot-spacer" />
          <button className="mv2-btn-cancel" onClick={onClose}>Cancel</button>
          {!form.multiProjectMode && (
            <button className="mv2-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create task"}
              {!saving && (
                <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 2, fontFamily: "monospace" }}>
                  ⌘↵
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Attachments upload modal */}
      {showAttModal && (
        <AttachmentsModal
          refType="task"
          refId={task.id}
          title="Task Attachments"
          autoOpenForm
          defaultCaption="Tasks"
          lockCaption
          onClose={() => { setShowAttModal(false); loadAttachments(); }}
        />
      )}

      {/* Confirm delete attachment */}
      {confirmDelAtt && (
        <div className="rd-confirm-overlay" onClick={() => setConfirmDelAtt(null)}>
          <div className="rd-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="rd-confirm-msg">
              Delete <strong>{confirmDelAtt.file_name}</strong>?
            </div>
            <div className="rd-confirm-actions">
              <button className="rd-att-btn rd-att-btn--danger" onClick={handleAttDelete} type="button">Delete</button>
              <button className="rd-att-btn rd-att-btn--ghost" onClick={() => setConfirmDelAtt(null)} type="button">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Log time modal */}
      {showTsModal && createPortal(
        <TimesheetEntryModal
          taskId={task.id}
          showToast={showToast}
          onClose={() => setShowTsModal(false)}
          onCreated={loadTsEntries}
        />,
        document.body
      )}

      {/* Due date change reason modal */}
      {dueDateModal && createPortal(
        <DueDateChangeModal
          fromDate={dueDateModal.fromDate}
          toDate={dueDateModal.toDate}
          onConfirm={handleDueDateConfirm}
          onCancel={() => setDueDateModal(null)}
        />,
        document.body
      )}

      {/* Multi-project selection + create modal */}
      {showMultiProjectModal && (
        <MultiProjectTaskModal
          projectOptions={projectOptions}
          users={users}
          resolveAssigneeTeam={resolveAssigneeTeam}
          isPrivateVisibility={isPrivateVisibility}
          loggedInUserName={loggedInUserName}
          taskCommon={{
            title: form.title,
            description: form.description,
            feature_id: form.feature_id || null,
            sub_feature_id: form.sub_feature_id || null,
            customer_due_date: form.customer_due_date || null,
            am_name: form.am_name,
            new_enhancement: form.new_enhancement,
            visibility: normalizeTaskVisibility(form.visibility),
            source: form.source || null,
            reporting_channel: form.reporting_channel || null,
            ...(() => {
              const parsed = parseEstimatedEffort(form.estimated_effort);
              return parsed ? { effort_hours: parsed.hours, effort_minutes: parsed.minutes } : {};
            })(),
          }}
          onSubmit={handleMultiProjectSubmit}
          onClose={() => setShowMultiProjectModal(false)}
        />
      )}

      {/* Related-defect quick view (Related Defects tab row click) */}
      <DefectQuickViewDrawer defectId={quickViewDefectId} onClose={() => setQuickViewDefectId(null)} />
    </>
  );
}

// ─── Toast component ──────────────────────────────────────────────────────────

function ToastContainer({ toasts, onUndo }) {
  return (
    <div className="mv2-toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`mv2-toast${t.type === "error" ? " mv2-toast--error" : ""}`}>
          <span>{t.msg}</span>
          {t.undoFn && (
            <button className="mv2-toast-undo" onClick={() => { t.undoFn(); onUndo(t.id); }}>
              Undo
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Filter Drawer ────────────────────────────────────────────────────────────

function FilterDrawer({ open, tasks, users, filterOptions = {}, appliedFilters, filterMode, onApply, onClose, onApplyTemplate }) {
  const [draft,            setDraft]            = useState({});
  const [mode,             setMode]             = useState("all");
  const [search,           setSearch]           = useState("");
  const [expanded,         setExpanded]         = useState(null);
  const [fieldSearch,      setFieldSearch]      = useState({});
  const [tagQuery,         setTagQuery]         = useState("");
  const [tagSuggestions,   setTagSuggestions]   = useState([]);
  const [tagLoadingSuggest, setTagLoadingSuggest] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(JSON.parse(JSON.stringify(appliedFilters)));
    setMode(filterMode);
    setSearch("");
    setExpanded(null);
    setFieldSearch({});
    setTagQuery("");
    setTagSuggestions([]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = tagQuery.trim();
    if (!q) { setTagSuggestions([]); return; }
    let active = true;
    setTagLoadingSuggest(true);
    listTags({ search: q })
      .then((rows) => { if (active) setTagSuggestions(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setTagSuggestions([]); })
      .finally(() => { if (active) setTagLoadingSuggest(false); });
    return () => { active = false; };
  }, [tagQuery]);

  const fields = useMemo(() => {
    return buildFilterFields(tasks, users, FILTER_FIELDS, filterOptions);
  }, [tasks, users, filterOptions]);

  const visibleFields = useMemo(
    () => buildVisibleFilterFields(fields, search, draft),
    [fields, search, draft]
  );

  const setOp = (key, op) =>
    setDraft((d) => ({ ...d, [key]: { op, values: d[key]?.values || [] } }));

  const toggleVal = (key, val) =>
    setDraft((d) => {
      const curr = d[key]?.values || [];
      const next = curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val];
      return { ...d, [key]: { op: d[key]?.op || "is", values: next } };
    });

  const clearField = (key) =>
    setDraft((d) => { const next = { ...d }; delete next[key]; return next; });

  const handleReset = () => { setDraft({}); setMode("all"); };
  const handleApply = () => { onApply(draft, mode); onClose(); };

  if (!open) return null;

  return (
    <>
      <div className="mv2-fdr-scrim" onClick={onClose} />
      <div className="mv2-fdr">
        {/* Header */}
        <div className="mv2-fdr-header">
          <span className="mv2-fdr-title">Filter</span>
          <button className="mv2-fdr-reset" onClick={handleReset}>Reset</button>
        </div>

        {/* Saved filter templates */}
        <div className="mv2-fdr-saved-wrap">
          <SavedFiltersPanel
            module="tasks"
            appliedFilters={draft}
            filterMode={mode}
            onApply={(filterDef, fm) => {
              setDraft(JSON.parse(JSON.stringify(filterDef)));
              setMode(fm || "all");
              onApplyTemplate?.(filterDef, fm || "all");
              onApply(filterDef, fm || "all");
              onClose();
            }}
          />
        </div>

        {/* Global field search */}
        <div className="mv2-fdr-search-wrap">
          <IconSearch />
          <input
            className="mv2-fdr-search"
            type="text"
            placeholder="Filter Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Field list */}
        <div className="mv2-fdr-list">
          {visibleFields.map((field) => {
            const isOpen    = expanded === field.key;
            const curr      = field.type === "daterange"
              ? (draft[field.key] || { from: "", to: "" })
              : field.type === "text"
                ? (draft[field.key] || { value: "" })
                : (draft[field.key] || { op: "is", values: [] });
            const fs        = fieldSearch[field.key] || "";
            // Alphabetical (field.options already sorted this way) with
            // currently-selected values pinned to the top — recomputed live
            // off curr.values so checking a box moves it up immediately.
            // No-op for daterange/text fields (field.options is always []
            // for those, curr.values is undefined).
            const opts      = (fs.trim()
              ? field.options.filter((o) => o.label.toLowerCase().includes(fs.toLowerCase()))
              : field.options
            ).slice().sort((a, b) => {
              const aSel = curr.values?.includes(a.value);
              const bSel = curr.values?.includes(b.value);
              if (aSel !== bSel) return aSel ? -1 : 1;
              return a.label.localeCompare(b.label);
            });
            const hasActive = field.type === "daterange"
              ? !!(curr.from || curr.to)
              : field.type === "text"
                ? !!curr.value?.trim()
                : curr.values?.length > 0;

            return (
              <div
                key={field.key}
                className={`mv2-fdr-field${isOpen ? " mv2-fdr-field--open" : ""}${hasActive ? " mv2-fdr-field--active" : ""}`}
              >
                <button
                  className="mv2-fdr-field-hdr"
                  onClick={() => setExpanded(isOpen ? null : field.key)}
                >
                  <span className="mv2-fdr-field-name">
                    {field.label}
                    {hasActive && (
                      <span className="mv2-fdr-badge">
                        {field.type === "daterange" || field.type === "text" ? "✓" : curr.values.length}
                      </span>
                    )}
                  </span>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {isOpen
                      ? <polyline points="18 15 12 9 6 15"/>
                      : <polyline points="9 18 15 12 9 6"/>
                    }
                  </svg>
                </button>

                {isOpen && (
                  <div className="mv2-fdr-field-body">
                    {field.type === "text" ? (
                      /* ── Text / contains search ── */
                      <div className="mv2-fdr-text-filter">
                        <div className="mv2-fdr-op-label">Contains</div>
                        <div className="mv2-fdr-val-search-wrap">
                          <IconSearch />
                          <input
                            className="mv2-fdr-val-search"
                            type="text"
                            placeholder={`Search ${field.label}...`}
                            value={curr.value || ""}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                [field.key]: { type: "text", value: e.target.value },
                              }))
                            }
                          />
                          {curr.value && (
                            <button
                              className="mv2-fdr-text-clear"
                              onClick={() =>
                                setDraft((d) => ({ ...d, [field.key]: { type: "text", value: "" } }))
                              }
                            >×</button>
                          )}
                        </div>
                      </div>
                    ) : field.type === "daterange" ? (
                      /* ── Date range picker ── */
                      <div className="mv2-fdr-daterange">
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">From</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.from || ""}
                            max={curr.to || undefined}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                [field.key]: { type: "daterange", ...d[field.key], from: e.target.value },
                              }))
                            }
                          />
                        </div>
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">To</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.to || ""}
                            min={curr.from || undefined}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                [field.key]: { type: "daterange", ...d[field.key], to: e.target.value },
                              }))
                            }
                          />
                        </div>
                        {(curr.from || curr.to) && (
                          <button
                            className="mv2-fdr-daterange-clear"
                            onClick={() =>
                              setDraft((d) => ({ ...d, [field.key]: { type: "daterange", from: "", to: "" } }))
                            }
                          >
                            Clear dates
                          </button>
                        )}
                      </div>
                    ) : field.key === "tags" ? (
                      /* ── Tags: async search + chips ── */
                      <>
                        <div className="mv2-fdr-op-row">
                          <select
                            className="mv2-fdr-op"
                            value={curr.op}
                            onChange={(e) => setOp(field.key, e.target.value)}
                          >
                            <option value="is">Is</option>
                            <option value="is_not">Is Not</option>
                          </select>
                          {hasActive && (
                            <button
                              type="button"
                              className="mv2-fdr-field-reset"
                              onClick={() => clearField(field.key)}
                            >
                              Reset this filter
                            </button>
                          )}
                        </div>

                        {curr.values.length > 0 && (
                          <div className="mv2-fdr-tag-chips">
                            {curr.values.map((v) => (
                              <span key={v} className="mv2-fdr-tag-chip">
                                {v}
                                <button
                                  type="button"
                                  className="mv2-fdr-tag-chip-x"
                                  onClick={() => toggleVal(field.key, v)}
                                >×</button>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="mv2-fdr-val-search-wrap">
                          <IconSearch />
                          <input
                            className="mv2-fdr-val-search"
                            type="text"
                            placeholder="Search tags…"
                            value={tagQuery}
                            onChange={(e) => setTagQuery(e.target.value)}
                          />
                        </div>

                        <div className="mv2-fdr-opts">
                          {tagLoadingSuggest && (
                            <div className="mv2-fdr-empty">Searching…</div>
                          )}
                          {!tagLoadingSuggest && tagQuery.trim() && tagSuggestions.filter((t) => !curr.values.includes(t.tag_name)).length === 0 && (
                            <div className="mv2-fdr-empty">No tags found</div>
                          )}
                          {!tagLoadingSuggest && !tagQuery.trim() && curr.values.length === 0 && (
                            <div className="mv2-fdr-empty">Type to search tags</div>
                          )}
                          {tagSuggestions
                            .filter((t) => !curr.values.includes(t.tag_name))
                            .map((t) => (
                              <label key={t.id} className="mv2-fdr-opt">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => {
                                    toggleVal(field.key, t.tag_name);
                                    setTagQuery("");
                                    setTagSuggestions([]);
                                  }}
                                />
                                <span>{t.tag_name}</span>
                              </label>
                            ))}
                        </div>
                      </>
                    ) : (
                      /* ── Checkbox list ── */
                      <>
                        <select
                          className="mv2-fdr-op"
                          value={curr.op}
                          onChange={(e) => setOp(field.key, e.target.value)}
                        >
                          <option value="is">Is</option>
                          <option value="is_not">Is Not</option>
                        </select>

                        {field.options.length > 5 && (
                          <div className="mv2-fdr-val-search-wrap">
                            <IconSearch />
                            <input
                              className="mv2-fdr-val-search"
                              type="text"
                              placeholder="Search..."
                              value={fs}
                              onChange={(e) =>
                                setFieldSearch((s) => ({ ...s, [field.key]: e.target.value }))
                              }
                            />
                          </div>
                        )}

                        <div className="mv2-fdr-opts">
                          {opts.map((opt) => (
                            <label key={opt.value} className="mv2-fdr-opt">
                              <input
                                type="checkbox"
                                checked={curr.values.includes(opt.value)}
                                onChange={() => toggleVal(field.key, opt.value)}
                              />
                              {field.key === "status" && (
                                <StatusDot status={opt.value} size={7} />
                              )}
                              <span>{opt.label}</span>
                            </label>
                          ))}
                          {opts.length === 0 && (
                            <div className="mv2-fdr-empty">No options</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Match mode */}
        <div className="mv2-fdr-mode">
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="mv2FdrMode"
              value="any"
              checked={mode === "any"}
              onChange={() => setMode("any")}
            />
            Any of these
          </label>
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="mv2FdrMode"
              value="all"
              checked={mode === "all"}
              onChange={() => setMode("all")}
            />
            All of these
          </label>
        </div>

        {/* Footer */}
        <div className="mv2-fdr-footer">
          <button className="mv2-fdr-find" onClick={handleApply}>Find</button>
          <button className="mv2-fdr-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MasterViewV2({ showToast: externalToast, user }) {
  const searchRef = useRef(null);

  // Remote data
  const [tasks,     setTasks]     = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [features,  setFeatures]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  // UI state
  const [tab,           setTab]           = useState(() => (isExternalUser(user) ? "all" : "mywork")); // all | myteam | mywork
  const [tabCounts,     setTabCounts]     = useState({});
  const [view,          setView]          = useState("table"); // table | board
  const [search,        setSearch]        = useState("");
  const [sortKey,       setSortKey]       = useState("customer_due_date");
  const [sortDir,       setSortDir]       = useState("asc");
  const [page,          setPage]          = useState(1);
  const [rowsPerPage,   setRowsPerPage]   = useState(50);
  const [totalCount,    setTotalCount]    = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawer,        setDrawer]        = useState(null);    // null | { mode: "new"|"edit", task?: {} }
  const [dragging,      setDragging]      = useState(null);    // task being dragged in board
  const [dragOver,      setDragOver]      = useState(null);    // column being dragged over
  const [collapsedCols, setCollapsedCols] = useState(new Set());
  const [toasts,        setToasts]        = useState([]);
  const [descPopup,     setDescPopup]     = useState(null); // { text, anchorRect }
  const [privateDescVisibleById, setPrivateDescVisibleById] = useState({});
  const descPopupRef = useRef(null);
  const deepLinkHandledRef = useRef(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );

  // Filter state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterOptions,    setFilterOptions]    = useState({});
  const filterOptionsStaleRef = useRef(true); // true = must re-fetch on next drawer open
  const [appliedFilters,   setAppliedFilters]   = useState({}); // {[fieldKey]: {op, values[]}}
  const [filterMode,       setFilterMode]       = useState("all");

  // Auto-apply default filter template on mount
  useEffect(() => {
    listFilterTemplates("tasks").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) {
        setAppliedFilters(def.filter_def || {});
        setFilterMode(def.filter_mode || "all");
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!descPopup) return;
    const onDocClick = (e) => {
      if (descPopupRef.current && !descPopupRef.current.contains(e.target)) {
        setDescPopup(null);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setDescPopup(null); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [descPopup]);

  // ── Toast helpers ───────────────────────────────────────────────────────────

  const addToast = useCallback((msg, type = "success", undoFn = null) => {
    const id = Date.now();
    setToasts((ts) => [...ts, { id, msg, type, undoFn }]);
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const TAB_VIEW = { all: "all", myteam: "team", mywork: "my" };

  useEffect(() => {
    if (isExternalUser(user) && tab === "mywork") {
      setTab("all");
      setPage(1);
    }
  }, [user, tab]);

  // Debounce search input — avoids an API call on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when any filter/sort/search/tab changes (not on page itself changing)
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (prevTabRef.current !== tab) { prevTabRef.current = tab; return; }
    setPage(1);
  }, [view, rowsPerPage, sortKey, sortDir, debouncedSearch, appliedFilters, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref that always holds the latest fetch-relevant state (avoids stale closures in useCallback)
  const fetchStateRef = useRef({});
  useEffect(() => {
    fetchStateRef.current = { tab, view, page, rowsPerPage, sortKey, sortDir, appliedFilters, debouncedSearch };
  }, [tab, view, page, rowsPerPage, sortKey, sortDir, appliedFilters, debouncedSearch]);

  function buildTasksApiParams(tabId) {
    const { view: v, page: p, rowsPerPage: rpp, sortKey: sk, sortDir: sd, appliedFilters: af, debouncedSearch: ds } = fetchStateRef.current;
    const isBoard = v === "board";
    const params = new URLSearchParams({ view: TAB_VIEW[tabId] || "all" });
    params.set("page",      isBoard ? 1   : p);
    params.set("page_size", isBoard ? 2000 : rpp);
    params.set("sort_by",  sk);
    params.set("sort_dir", sd);
    if (ds) params.set("q", ds);
    const filters = af || {};
    const addChoice = (key, inP, notInP) => {
      const f = filters[key];
      if (!f || !Array.isArray(f.values) || f.values.length === 0) return;
      const vals = f.values.filter(Boolean);
      if (!vals.length) return;
      if      (f.op === "is")     params.set(inP,    vals.join(","));
      else if (f.op === "is_not") params.set(notInP, vals.join(","));
    };
    addChoice("status",          "status_in",          "status_not_in");
    addChoice("customer",        "customer_id_in",     "customer_id_not_in");
    addChoice("project",         "project_id_in",      "project_id_not_in");
    addChoice("feature",         "feature_id_in",      "feature_id_not_in");
    addChoice("sub_feature",     "sub_feature_id_in",  "sub_feature_id_not_in");
    addChoice("tmm_team_name",   "team_name_in",       "team_name_not_in");
    addChoice("tmm_member",      "member_in",          "member_not_in");
    addChoice("am_name",         "am_name_in",         "am_name_not_in");
    addChoice("new_enhancement", "type_in",            "type_not_in");
    addChoice("visibility",      "visibility_in",      "visibility_not_in");
    addChoice("created_by_name", "created_by_name_in", "created_by_name_not_in");
    addChoice("epic_name",       "epic_name_in",       "epic_name_not_in");
    addChoice("tags",            "tag_names_in",       "tag_names_not_in");
    addChoice("project_status",  "project_status_in",  "project_status_not_in");
    addChoice("epic_status",     "epic_status_in",     "epic_status_not_in");
    const dueDef = filters["customer_due_date"];
    if (dueDef?.from) params.set("due_from", dueDef.from);
    if (dueDef?.to)   params.set("due_to",   dueDef.to);
    return params;
  }

  const fetchTasks = useCallback(async (tabId) => {
    setLoading(true);
    try {
      const params = buildTasksApiParams(tabId);
      const res = await fetch(`${API}/tasks?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const json = await res.json();
      const items = Array.isArray(json) ? json : (json.items || []);
      const total = json.total ?? items.length;
      const data  = items.map(normalizeTaskRecord);
      setTasks(data);
      setTotalCount(total);
      setTabCounts((prev) => ({ ...prev, [tabId]: total }));
    } catch {
      addToast("Failed to load tasks", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load — fetch tasks + static reference data in parallel
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = buildTasksApiParams(tab);
        const [tasksRes, custRes, usersRes, featRes] = await Promise.all([
          fetch(`${API}/tasks?${params}`, { credentials: "include" }),
          fetch(`${API}/customers-projects?active_only=true${user?.db_user_id ? `&user_id=${encodeURIComponent(user.db_user_id)}` : ""}`, { credentials: "include" }),
          fetch(`${API}/users/dropdown`,              { credentials: "include" }),
          fetch(`${API}/features/all-with-subs`,       { credentials: "include" }),
        ]);
        if (cancelled) return;
        const [tasksJson, custData, usersData, featData] = await Promise.all([
          tasksRes.ok ? tasksRes.json() : { items: [], total: 0 },
          custRes.ok  ? custRes.json()  : [],
          usersRes.ok ? usersRes.json() : [],
          featRes.ok  ? featRes.json()  : { features: [] },
        ]);
        if (cancelled) return;
        const items = Array.isArray(tasksJson) ? tasksJson : (tasksJson.items || []);
        const total = tasksJson.total ?? items.length;
        const tasksData = items.map(normalizeTaskRecord);
        setTasks(tasksData);
        setTotalCount(total);
        setTabCounts((prev) => ({ ...prev, [tab]: total }));
        setCustomers(custData);
        setUsers(usersData);
        setFeatures(featData.features || []);
      } catch {
        if (!cancelled) addToast("Some data failed to load", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch whenever any param that affects the result changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    fetchTasks(tab);
  }, [tab, view, page, rowsPerPage, sortKey, sortDir, debouncedSearch, appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazily fetch filter options — only when the filter drawer is opened AND options are stale.
  // Marked stale after any task write so the next open always gets fresh values.
  useEffect(() => {
    if (!filterDrawerOpen || !filterOptionsStaleRef.current) return;
    const params = new URLSearchParams({ view: TAB_VIEW[tab] || "all" });
    fetch(`${API}/tasks/filter-options?${params}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        setFilterOptions(data || {});
        filterOptionsStaleRef.current = false;
      })
      .catch(() => {}); // silent — falls back to current-page options
  }, [filterDrawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark filter options stale whenever the tab changes (different scope = different options)
  useEffect(() => { filterOptionsStaleRef.current = true; }, [tab]);

  useEffect(() => {
    if (tab !== "all") return;
    setAppliedFilters((prev) => {
      if (prev?.status) return prev;
      return { ...DEFAULT_ALL_TASKS_APPLIED_FILTERS, ...(prev || {}) };
    });
  }, [tab]);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const openTaskId = params.get("openTaskId") || params.get("taskId") || params.get("task_id");
    if (!openTaskId) {
      deepLinkHandledRef.current = true;
      return;
    }
    if (loading) return;
    deepLinkHandledRef.current = true;

    const cleanupUrl = () => {
      params.delete("openTaskId");
      params.delete("taskId");
      params.delete("task_id");
      const nextQs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${nextQs ? `?${nextQs}` : ""}`);
    };

    const localTask = (tasks || []).find((t) => String(t.id) === String(openTaskId));
    if (localTask) {
      setTab("all");
      setView("table");
      setDrawer({ mode: "edit", task: localTask });
      cleanupUrl();
      return;
    }

    // Not in the currently-loaded/filtered page of tasks (e.g. a deep link
    // from Reports can point at any task org-wide, not just what's in the
    // caller's current view/filter) — fetch it directly instead of giving up.
    tasksApi.getById(openTaskId)
      .then((task) => {
        setTab("all");
        setView("table");
        setDrawer({ mode: "edit", task });
      })
      .catch(() => addToast("Task not found for deep link", "warn"))
      .finally(cleanupUrl);
  }, [loading, tasks, addToast]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      const active = document.activeElement;
      const inInput = ["INPUT", "TEXTAREA", "SELECT"].includes(active?.tagName);

      if (!inInput && e.key === "n" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setDrawer({ mode: "new" });
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Resolve username → display name ────────────────────────────────────────

  const resolveDisplayName = useCallback(
    (userName) => {
      if (!userName) return "";
      const u = users.find((u) => u.user_name === userName);
      return u?.display_name || userName;
    },
    [users]
  );

  // ── Server-side filtering/sorting/pagination ─────────────────────────────────
  // tasks = the current page returned by the API (already filtered, sorted, paginated)
  // filteredTasks = tasks (for board view compatibility — board groups these by status)
  // pageTasks     = tasks (no client-side slicing needed)
  // pageCount     = derived from server-provided totalCount

  const visibleTasks  = tasks; // server already applied visibility scope
  const filteredTasks = tasks; // server already filtered
  const pageTasks     = tasks; // server already paginated
  const pageCount     = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Auto-collapse empty board columns when switching to board view
  useEffect(() => {
    if (view !== "board") return;
    setCollapsedCols(buildCollapsedBoardCols(tasks, BOARD_COLS));
  }, [view, tasks]);

  // Tab counts — persisted per tab so switching away doesn't reset the badge

  // ── Sort toggle ─────────────────────────────────────────────────────────────

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const openDescriptionPopup = (e, text) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDescPopup({ text, anchorRect: rect });
  };

  const isPrivateDescVisible = useCallback(
    (taskId) => !!privateDescVisibleById[taskId],
    [privateDescVisibleById]
  );

  const togglePrivateDescVisibility = useCallback((taskId) => {
    setPrivateDescVisibleById((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  }, []);

  // ── Selection ───────────────────────────────────────────────────────────────

  // ── Inline edit commit ──────────────────────────────────────────────────────

  const handleInlineCommit = useCallback(async (taskId, field, value) => {
    const prev = tasks.find((t) => t.id === taskId);
    if (!prev) return;
    const nextValue = field === "visibility" ? normalizeTaskVisibility(value) : value;

    // Optimistic update
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, [field]: nextValue } : t)));

    try {
      let res;
      if (field === "status") {
        res = await fetch(`${API}/tasks/${taskId}/status`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: value }),
        });
      } else {
        res = await fetch(`${API}/tasks/${taskId}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [field]: nextValue }),
        });
      }
      if (!res.ok) throw new Error();
      addToast("Changes saved");
    } catch {
      // Rollback
      setTasks((ts) => ts.map((t) => (t.id === taskId ? prev : t)));
      addToast("Failed to save changes", "error");
    }
  }, [tasks, addToast]);

  // ── Create task ─────────────────────────────────────────────────────────────

  const handleCreate = async (form, pendingTagIds = []) => {
    const payload = {
      ...form,
      visibility: normalizeTaskVisibility(form.visibility),
    };
    try {
      const res = await fetch(`${API}/tasks`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      const createdId = created?.id;
      if (createdId && pendingTagIds.length > 0) {
        await Promise.allSettled(
          pendingTagIds.map((tagId) =>
            createTagMapping({ module_name: "tasks", entity_id: createdId, tag_id: tagId })
          )
        );
      }
      setDrawer(null);
      addToast("Task created");
      filterOptionsStaleRef.current = true;
      await fetchTasks(tab);
    } catch {
      addToast("Failed to create task", "error");
    }
  };

  const handleCreateMultiProject = async (payload) => {
    try {
      const res = await fetch(`${API}/tasks/multi-project`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || "Failed to create tasks");
      }
      const result = await res.json();
      const count = result?.created?.length || 0;
      setDrawer(null);
      addToast(`Created ${count} task${count === 1 ? "" : "s"} across ${count} project${count === 1 ? "" : "s"}`);
      filterOptionsStaleRef.current = true;
      await fetchTasks(tab);
    } catch (err) {
      addToast(err.message || "Failed to create tasks", "error");
    }
  };

  // ── Update task ─────────────────────────────────────────────────────────────

  const handleUpdate = async (form) => {
    // eslint-disable-next-line no-unused-vars
    const { tag_names: _tag_names, ...formWithoutTags } = form;
    const payload = {
      ...formWithoutTags,
      visibility: normalizeTaskVisibility(form.visibility),
    };
    const { id } = payload;
    const prev = tasks.find((t) => t.id === id);
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...payload } : t)));
    try {
      const res = await fetch(`${API}/tasks/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setDrawer(null);
      filterOptionsStaleRef.current = true;
      addToast("Changes saved");
    } catch {
      setTasks((ts) => ts.map((t) => (t.id === id ? prev : t)));
      addToast("Failed to save changes", "error");
    }
  };

  const handleSaveDrawer = (form, pendingTagIds = []) => {
    if (form.id) return handleUpdate(form);
    return handleCreate(form, pendingTagIds);
  };

  // ── Delete task ─────────────────────────────────────────────────────────────

  const handleDelete = async (task) => {
    const label = (task?.title || task?.description || "task").slice(0, 80);
    const confirmed = window.confirm(`Delete "${label}"?`);
    if (!confirmed) return;

    const prev = [...tasks];
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    setDrawer(null);
    addToast(
      `Deleted "${(task.title || task.description || "task").slice(0, 40)}"`,
      "success",
      async () => {
        // Undo: restore the task locally (we can't truly undo a DELETE on server,
        // but we can PUT it back with is_deleted=false if the API supports it)
        setTasks(prev);
      }
    );
    try {
      const res = await fetch(`${API}/tasks/${task.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
    } catch {
      setTasks(prev);
      addToast("Failed to delete task", "error");
    }
  };

  // ── Board drag & drop ───────────────────────────────────────────────────────

  const handleDragStart = (task) => setDragging(task);
  const handleDragOver  = (e, col) => { e.preventDefault(); setDragOver(col); };
  const handleDrop      = async (col) => {
    if (!dragging || dragging.status === col) { setDragging(null); setDragOver(null); return; }
    const prev = { ...dragging };
    setTasks((ts) =>
      ts.map((t) => (t.id === dragging.id ? { ...t, status: col } : t))
    );
    setDragging(null);
    setDragOver(null);
    try {
      const res = await fetch(`${API}/tasks/${dragging.id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: col }),
      });
      if (!res.ok) throw new Error();
      addToast("Status updated");
    } catch {
      setTasks((ts) => ts.map((t) => (t.id === prev.id ? prev : t)));
      addToast("Failed to update status", "error");
    }
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  // Server-side export: streams every task matching the current filters
  // (not just the currently-loaded page) as CSV.
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = buildTasksApiParams(tab);
      const res = await fetch(`${API}/tasks/export?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tasks_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addToast("Failed to export tasks", "error");
    } finally {
      setExporting(false);
    }
  }, [tab, addToast]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Table columns ───────────────────────────────────────────────────────────

  const COLS = [
    { key: "task_code",       label: "Task ID",     width: 116, sortable: true },
    { key: "title",           label: "Title",       width: 220, sortable: true },
    { key: "description",     label: "Description", width: 220, sortable: true },
    { key: "customer",        label: "Customer",    width: 120, sortable: true },
    { key: "project",         label: "Project",     width: 130, sortable: true },
    { key: "feature",         label: "Feature",     width: 120, sortable: true },
    { key: "sub_feature",     label: "Sub Feature", width: 140, sortable: true },
    { key: "tmm_team_name",   label: "Team",        width: 110, sortable: true },
    { key: "tmm_member",      label: "Assignee",    width: 140, sortable: true },
    { key: "status",           label: "Status",      width: 120, sortable: true },
    { key: "new_enhancement", label: "Type",        width: 100, sortable: true },
    { key: "visibility",      label: "Visibility",  width: 110, sortable: true },
    { key: "created_by_name", label: "Created By",  width: 140, sortable: true },
    { key: "customer_due_date", label: "Due Date",  width: 110, sortable: true },
  ];
  const allBoardColsCollapsed = areAllBoardColsCollapsed(collapsedCols, BOARD_COLS);
  const activeFilterCount = countActiveFilters(appliedFilters);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="mv2-root" style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}>

      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(c => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
        taskCount={tasks.length}
      />

      {/* ── MAIN ── */}
      <div className="mv2-main">

        {/* Editorial header */}
        <div className="page-head">
          <h1>
            Tasks
            {" "}
            <em>{totalCount}</em>
          </h1>
          <div className="page-head-actions">
            {/* View toggle */}
            <div className="mv2-view-toggle">
              <button
                className={`mv2-view-btn${view === "table" ? " mv2-view-btn--active" : ""}`}
                onClick={() => setView("table")}
                title="Table view"
              >
                <IconTable />
              </button>
              <button
                className={`mv2-view-btn${view === "board" ? " mv2-view-btn--active" : ""}`}
                onClick={() => setView("board")}
                title="Board view"
              >
                <IconKanban />
              </button>
            </div>

            {/* Export */}
            <button className="mv2-export-btn" onClick={handleExport} disabled={exporting}>
              <IconDownload />
              {exporting ? "Exporting…" : "Export"}
            </button>

            {/* New task */}
            <button
              className="mv2-new-task-btn v2-btn-primary"
              onClick={() => setDrawer({ mode: "new" })}
            >
              <IconPlus />
              New task
              <span className="mv2-kbd">N</span>
            </button>
          </div>
        </div>

        {/* Toolbar: tabs + search/filter */}
        <div className="page-toolbar">
          {/* Tabs */}
          <div className="page-tabs">
            {[
              { id: "all",    label: "All"     },
              { id: "myteam", label: "My team", hideForExternal: true },
              { id: "mywork", label: "My work" },
            ].filter(({ hideForExternal }) => !(hideForExternal && isExternalUser(user))).map(({ id, label }) => (
              <button
                key={id}
                className={`page-tab${tab === id ? " page-tab--active" : ""}`}
                onClick={() => { setTab(id); setPage(1); }}
              >
                {label}
                {tabCounts[id] != null && (
                  <span className="page-tab-count">{tabCounts[id]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Board collapse-all toggle (inline with tabs when board view) */}
          {view === "board" && (
            <button
              className="mv2-board-collapse-all-btn"
              title={allBoardColsCollapsed ? "Expand all columns" : "Collapse all columns"}
              onClick={() => {
                setCollapsedCols(getCollapseAllSet(!allBoardColsCollapsed, BOARD_COLS));
              }}
            >
              {allBoardColsCollapsed ? "Expand All" : "Collapse All"}
            </button>
          )}

          {/* Right side: search + filter */}
          <div className="page-toolbar-right">
            <div className="mv2-toolbar-search">
              <svg className="mv2-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchRef}
                className="mv2-search-input"
                placeholder="Search tasks…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <span className="mv2-search-kbd">⌘K</span>
            </div>

            {/* Filter button */}
            <FilterTriggerButton
              activeCount={activeFilterCount}
              onClick={() => setFilterDrawerOpen(true)}
              onClear={() => {
                setAppliedFilters({});
                setFilterMode("all");
              }}
            />
          </div>
        </div>

        {/* Content area — scroll body isolates horizontal scroll from tabs */}
        <div className="mv2-scroll-body">
        {loading ? (
          <div className="mv2-loading">
            <div className="mv2-spinner" />
            Loading tasks…
          </div>
        ) : view === "table" ? (
          <>
            {/* Table */}
            <div className="page-table-wrap">
              <table className="page-table">
                <thead>
                  <tr>
                    {COLS.map((col) => (
                      <th
                        key={col.key}
                        style={{ width: col.width }}
                        className={`${col.sortable ? "page-th-sortable" : ""}${sortKey === col.key ? " page-th--sorted" : ""}`}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <div className="page-th-inner">
                          {col.label}
                          {col.sortable && (
                            <span className={`page-sort-icon${sortKey === col.key ? " page-sort-icon--active" : ""}`}>
                              {sortKey === col.key && sortDir === "asc" ? <IconChevronUp /> : <IconChevronDown />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageTasks.length === 0 ? (
                    <tr>
                      <td colSpan={COLS.length}>
                        <div className="mv2-empty-state">
                          <svg viewBox="0 0 64 64" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="10" y="10" width="44" height="44" rx="6"/>
                            <line x1="20" y1="24" x2="44" y2="24"/>
                            <line x1="20" y1="32" x2="44" y2="32"/>
                            <line x1="20" y1="40" x2="36" y2="40"/>
                          </svg>
                          <div className="mv2-empty-title">No tasks found</div>
                          <div className="mv2-empty-sub">
                            {search || Object.values(appliedFilters).some((f) => f.type === "daterange" ? !!(f.from || f.to) : f.type === "text" ? !!f.value?.trim() : f.values?.length > 0)
                              ? "Try adjusting your filters"
                              : "Get started by creating your first task"}
                          </div>
                          {!search && !Object.values(appliedFilters).some((f) => f.type === "daterange" ? !!(f.from || f.to) : f.type === "text" ? !!f.value?.trim() : f.values?.length > 0) && (
                            <button
                              className="mv2-empty-btn"
                              onClick={() => setDrawer({ mode: "new" })}
                            >
                              Add task
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageTasks.map((task) => (
                      <tr key={task.id}>

                        {/* Task ID */}
                        <td style={{ width: 116 }} className="tc-code">
                          <span className="mv2-cell-text" title={task.task_code || task.id}>
                            {task.task_code || "—"}
                          </span>
                        </td>

                        {/* Title */}
                        <td style={{ width: 220 }} className="tc-desc">
                          <InlineCell
                            value={task.title}
                            field="title"
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <span
                              className="mv2-desc-cell"
                              onClick={() => setDrawer({ mode: "edit", task })}
                            >
                              <span className="mv2-desc-clamp-wrap">
                                <span className={`mv2-desc-text${(task.title || "").length > 80 ? " mv2-desc-text--more" : ""}`}>
                                  {task.title || "—"}
                                </span>
                              </span>
                            </span>
                          </InlineCell>
                        </td>

                        {/* Description */}
                        <td style={{ width: 220 }} className="tc-desc">
                          <InlineCell
                            value={task.description}
                            field="description"
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            {normalizeTaskVisibility(task.visibility) === "Private" && !isPrivateDescVisible(task.id) ? (
                              <span className="mv2-desc-cell">
                                <span className="mv2-desc-clamp-wrap">
                                  <span className="mv2-desc-text">xxxx....</span>
                                  <button
                                    type="button"
                                    className="page-row-action-btn"
                                    title="Show description"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      togglePrivateDescVisibility(task.id);
                                    }}
                                  >
                                    <IconEye />
                                  </button>
                                </span>
                              </span>
                            ) : (
                            <span
                              className="mv2-desc-cell"
                              onClick={() => setDrawer({ mode: "edit", task })}
                            >
                              <span className="mv2-desc-clamp-wrap">
                                <span className={`mv2-desc-text${(task.description || "").length > 80 ? " mv2-desc-text--more" : ""}`}>
                                  {task.description || "—"}
                                </span>
                                {normalizeTaskVisibility(task.visibility) === "Private" && (
                                  <button
                                    type="button"
                                    className="page-row-action-btn"
                                    title="Hide description"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      togglePrivateDescVisibility(task.id);
                                    }}
                                  >
                                    <IconEyeOff />
                                  </button>
                                )}
                                {(task.description || "").length > 80 && (
                                  <button
                                    type="button"
                                    className="mv2-desc-more"
                                    onClick={(e) => openDescriptionPopup(e, task.description)}
                                  >
                                    ...(more)
                                  </button>
                                )}
                              </span>
                            </span>
                            )}
                          </InlineCell>
                        </td>

                        {/* Customer */}
                        <td style={{ width: 120 }}>
                          <InlineCell
                            value={task.customer}
                            field="customer"
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <span className="mv2-customer-cell">{task.customer || "—"}</span>
                          </InlineCell>
                        </td>

                        {/* Project */}
                        <td style={{ width: 130 }}>
                          <InlineCell
                            value={task.project}
                            field="project"
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <span className="mv2-cell-text">{task.project || "—"}</span>
                          </InlineCell>
                        </td>

                        {/* Feature */}
                        <td style={{ width: 120 }}>
                          <span className="mv2-cell-text" title={task.feature}>{task.feature || "—"}</span>
                        </td>

                        {/* Sub Feature */}
                        <td style={{ width: 140 }}>
                          <InlineCell
                            value={task.sub_feature}
                            field="sub_feature"
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <span className="mv2-cell-text" title={task.sub_feature}>{task.sub_feature || "—"}</span>
                          </InlineCell>
                        </td>

                        {/* Team */}
                        <td style={{ width: 110 }}>
                          <span title={task.tmm_team_name}>{task.tmm_team_name || "—"}</span>
                        </td>

                        {/* Assignee */}
                        <td style={{ width: 130 }}>
                          {task.tmm_member ? (
                            <div className="mv2-assignee-cell">
                              <div className={`mv2-assignee-avatar ac-${avatarColor(resolveDisplayName(task.tmm_member))}`}>
                                {getInitials(resolveDisplayName(task.tmm_member))}
                              </div>
                              <span className="mv2-assignee-name" title={resolveDisplayName(task.tmm_member)}>
                                {resolveDisplayName(task.tmm_member)}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "var(--mv2-text-faint)" }}>—</span>
                          )}
                        </td>

                        {/* Status */}
                        <td style={{ width: 120 }}>
                          <InlineCell
                            value={task.status}
                            field="status"
                            options={IMPL_STATUS_OPTIONS}
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <StatusPill status={task.status} />
                          </InlineCell>
                        </td>

                        {/* Type */}
                        <td style={{ width: 100 }}>
                          <InlineCell
                            value={task.new_enhancement}
                            field="new_enhancement"
                            options={TYPE_OPTIONS}
                            onCommit={(f, v) => handleInlineCommit(task.id, f, v)}
                          >
                            <TypePill type={task.new_enhancement} />
                          </InlineCell>
                        </td>

                        {/* Visibility */}
                        <td style={{ width: 110 }}>
                          <InlineCell
                            value={normalizeTaskVisibility(task.visibility)}
                            field="visibility"
                            options={TASK_VISIBILITY_OPTIONS}
                            onCommit={(f, v) => handleInlineCommit(task.id, f, normalizeTaskVisibility(v))}
                          >
                            <span className="mv2-cell-text">{normalizeTaskVisibility(task.visibility)}</span>
                          </InlineCell>
                        </td>

                        {/* Created By */}
                        <td style={{ width: 140 }}>
                          <span className="mv2-cell-text" title={task.created_by_name}>{task.created_by_name || "—"}</span>
                        </td>

                        {/* Due date + hover actions */}
                        <td style={{ width: 110 }}>
                          <div className="mv2-due-cell">
                            <span className={`mv2-date-cell${dueDateClass(task.customer_due_date, task.status)}`}>
                              {fmtDate(task.customer_due_date) || "—"}
                            </span>
                            <div className="mv2-due-actions">
                              <button
                                className="page-row-action-btn"
                                title="Edit"
                                onClick={() => setDrawer({ mode: "edit", task })}
                              >
                                <IconEdit />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </>
        ) : (
          /* ── BOARD VIEW ── */
          <div className="mv2-board">
            {BOARD_COLS.map((col) => {
              const colTasks = filteredTasks.filter((t) => t.status === col);
              const isCollapsed = collapsedCols.has(col);
              const toggleCollapse = (e) => {
                e.stopPropagation();
                setCollapsedCols((prev) => {
                  const next = new Set(prev);
                  if (next.has(col)) next.delete(col); else next.add(col);
                  return next;
                });
              };

              if (isCollapsed) {
                return (
                  <div
                    key={col}
                    className="mv2-board-col mv2-board-col--collapsed"
                    onClick={toggleCollapse}
                    title={`${col} (${colTasks.length}) — click to expand`}
                    onDragOver={(e) => { handleDragOver(e, col); }}
                    onDrop={() => handleDrop(col)}
                    onDragLeave={() => setDragOver(null)}
                  >
                    <div className="mv2-board-col-collapsed-inner">
                      <span className="mv2-board-col-collapsed-label">
                        {col} ({colTasks.length})
                      </span>
                      <StatusDot status={col} size={7} />
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={col}
                  className={`mv2-board-col${dragOver === col ? " mv2-board-col--over" : ""}`}
                  onDragOver={(e) => handleDragOver(e, col)}
                  onDrop={() => handleDrop(col)}
                  onDragLeave={() => setDragOver(null)}
                >
                  <div className="mv2-board-col-header">
                    <StatusDot status={col} size={8} />
                    <span className="mv2-board-col-label">{col}</span>
                    <span className="mv2-board-col-count">{colTasks.length}</span>
                    <button
                      className="mv2-board-add-btn"
                      title={`Add ${col} task`}
                      onClick={() =>
                        setDrawer({ mode: "new", prefill: { status: col } })
                      }
                    >+</button>
                    <button
                      className="mv2-board-collapse-btn"
                      title="Collapse column"
                      onClick={toggleCollapse}
                    >
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </button>
                  </div>
                  <div className="mv2-board-cards">
                    {colTasks.map((task) => (
                      <div
                        key={task.id}
                        className={`mv2-board-card${dragging?.id === task.id ? " mv2-board-card--dragging" : ""}`}
                        draggable
                        onDragStart={() => handleDragStart(task)}
                        onDragEnd={() => { setDragging(null); setDragOver(null); }}
                        onClick={() => setDrawer({ mode: "edit", task })}
                      >
                        <div className="mv2-board-card-header">
                          <span className={`mv2-board-card-due${dueDateClass(task.customer_due_date, task.status).replace("mv2-date--", "mv2-board-card-due--")}`}>
                            {task.customer_due_date
                              ? new Date(task.customer_due_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                              : "No due date"}
                          </span>
                          {(task.estimated_hours > 0 || task.estimated_minutes > 0) && (
                            <span className="mv2-board-card-effort">
                              Est: {task.estimated_hours > 0 ? `${task.estimated_hours}h` : ""}
                              {task.estimated_minutes > 0 ? ` ${task.estimated_minutes}m` : ""}
                            </span>
                          )}
                        </div>
                        <div className="mv2-board-card-desc">{task.title || task.description}</div>
                        <div className="mv2-board-card-meta">
                          <TypePill type={task.new_enhancement} />
                          {task.customer && (
                            <span className="mv2-board-card-customer">{task.customer}</span>
                          )}
                          {task.tmm_member && (
                            <span className="mv2-board-card-assignee" title={resolveDisplayName(task.tmm_member)}>
                              {getInitials(resolveDisplayName(task.tmm_member))}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {colTasks.length === 0 && (
                      <div style={{
                        padding: "20px 8px",
                        textAlign: "center",
                        color: "var(--mv2-text-faint)",
                        fontSize: 12,
                        borderRadius: "var(--mv2-radius)",
                        border: "1.5px dashed var(--mv2-border)",
                      }}>
                        Drop tasks here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>{/* /mv2-scroll-body */}

        {/* Table footer — outside scroll body so it doesn't shift on horizontal scroll */}
        {!loading && view === "table" && (
          <div className="mv2-table-footer">
            <span className="mv2-footer-info">
              Showing {totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1}–
              {Math.min(page * rowsPerPage, totalCount)} of {totalCount} tasks
            </span>
            <div className="mv2-footer-rpp">
              <span>Rows per page</span>
              <select
                className="mv2-footer-select"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="mv2-pagination">
              <button
                className="mv2-page-btn"
                disabled={page === 1}
                onClick={() => setPage(1)}
                title="First page"
              >«</button>
              <button
                className="mv2-page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                title="Previous page"
              >‹</button>
              {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
                let p;
                if (pageCount <= 7) { p = i + 1; }
                else if (page <= 4) { p = i + 1; }
                else if (page >= pageCount - 3) { p = pageCount - 6 + i; }
                else { p = page - 3 + i; }
                return (
                  <button
                    key={p}
                    className={`mv2-page-btn${p === page ? " mv2-page-btn--active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="mv2-page-btn"
                disabled={page === pageCount}
                onClick={() => setPage((p) => p + 1)}
                title="Next page"
              >›</button>
              <button
                className="mv2-page-btn"
                disabled={page === pageCount}
                onClick={() => setPage(pageCount)}
                title="Last page"
              >»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── DRAWER ── */}
      {drawer && (
        <TaskDrawer
          task={drawer.mode === "edit" ? drawer.task : (drawer.prefill ? { ...makeEmpty(user), ...drawer.prefill } : null)}
          customers={customers}
          users={users}
          features={features}
          user={user}
          onClose={() => setDrawer(null)}
          onSave={handleSaveDrawer}
          onSaveMultiProject={handleCreateMultiProject}
          onDelete={handleDelete}
          showToast={addToast}
        />
      )}

      {/* ── FILTER DRAWER ── */}
        <FilterDrawer
          open={filterDrawerOpen}
          tasks={visibleTasks}
          users={users}
          filterOptions={filterOptions}
          appliedFilters={appliedFilters}
          filterMode={filterMode}
          onApply={(filters, mode) => { setAppliedFilters(filters); setFilterMode(mode); }}
          onApplyTemplate={(filters, mode) => { setAppliedFilters(filters); setFilterMode(mode); }}
          onClose={() => setFilterDrawerOpen(false)}
        />

      {descPopup && createPortal(
        <div
          ref={descPopupRef}
          className="mv2-desc-popup"
          style={{
            position: "fixed",
            top: Math.min(descPopup.anchorRect.bottom + 8, window.innerHeight - 240),
            left: Math.max(12, Math.min(descPopup.anchorRect.left, window.innerWidth - 380)),
            zIndex: 10000,
          }}
        >
          <div className="mv2-desc-popup-title">Description</div>
          <div className="mv2-desc-popup-body">{descPopup.text}</div>
        </div>,
        document.body
      )}

      {/* ── TOASTS ── */}
      <ToastContainer toasts={toasts} onUndo={removeToast} />
    </div>
  );
}
